import React, { useState, useMemo, useEffect, useCallback, useRef, useLayoutEffect } from "react";
import { Flag16Regular, Clock16Regular } from "@fluentui/react-icons";
import { vorgangService, VorgangDto, type FeedAustauschFilter, type VorgangFeedItem } from "../services/vorgangService";
import { useInfiniteRows } from "../hooks/useInfiniteRows";
import { useNavigate } from "react-router";
import { openVorgangCreateFlow } from "./vorgang/create/VorgangCreateFlowWindow";
import { useWindowManager } from "./windowManager/WindowManagerContext";
import { useAuth } from "../context/AuthContext";
import { listAppUsers, userDisplay, type AppUser } from "../services/userService";
import { type ScopeKey, bereichOptionen, defaultVorgangScope } from "../utils/vorgangScope";
import { VorgangProgressMini, VorgangStatusPill } from "./vorgang/shared/StatusCell";
import { betreffOhneArt, zeigtArtChip } from "../data/vorgangArt";
import { PeopleCell, unionPeople } from "./vorgang/shared/PeopleCell";
import { FoBoBadge } from "./shared/foBo";
import { personenFilterOptionen } from "./vorgang/shared/people";
import { InitialsAvatar } from "./chat/chatUi";
import { REFINED } from "./risikoanalyseStep/tokens";
import { useResponsive } from "../hooks/useResponsive";
import { deDate as deIsoDate, isOverdueFrist } from "./dashboard/DashboardShared";
import { DateCell, DateEditCell } from "./vorgang/shared/DateCells";
import { baueVorgangZeilen, zeilenLink, type UnifiedRow } from "./vorgang/shared/unifiedRows";
import { AustauschChip } from "./vorgang/shared/AustauschChip";
import {
    DataTable,
    ColumnsMenu,
    type Column,
    type SortState,
} from "./redesign/table";
import { Checkbox } from "./redesign/table/cells";
import { TableBottomBar } from "./redesign/table/TableBottomBar";
import { VorgaengeZusammenfuehrenDialog } from "./vorgang/VorgaengeZusammenfuehrenDialog";
import { TodoZusammenfuehrenDialog } from "./vorgang/v4/TodoZusammenfuehrenDialog";
import { type PeopleFilterOption, type BoFoOption } from "./redesign/table/RdColumnFilter";
import {
    DATE_PRESETS,
    PERSONEN_ROLLE_PRAEFIX,
    datePresetRange,
    matchingDatePreset,
    type DateRangeValue,
} from "./redesign/table/filterModel";
import { I } from "./redesign/kvpIcons";
import { ConfirmDialog } from "./redesign/ConfirmDialog";
import { PapierkorbDialog } from "./vorgang/PapierkorbDialog";
import {
    darfHierher,
    istVorgangZug,
    verschmelzErklaerung,
    verschmelzFrage,
    ZIEHTYP_VORGANG,
    tauscheZug,
    alsTodoErklaerung,
    alsTodoFrage,
    zugWeg,
    type VorgangZug,
} from "./vorgang/shared/vorgangZug";
import {
    darfAufTodo,
    darfTodoHierher,
    istTodoZug,
    verschiebeErklaerung,
    verschiebeFrage,
    ZIEHTYP_TODO,
    type TodoZug,
} from "./vorgang/shared/todoZug";
import { zielNachVerschmelzen } from "./vorgang/shared/verschmelzAnzeige";
import {
    IMPORTIERT,
    OHNE_IMPORT,
    STATUS_OPTIONS,
    passtZumStatusFilter,
    statusFilterUmschalten,
    statusFilterWerte,
    vorgangNachStatus,
} from "./vorgang/shared/statusFilter";
import { deriveVorgangStatus } from "./vorgang/shared/vorgangStatus";
import type { SammelSkip } from "../services/vorgangService";
import { Toast } from "./redesign/Toast";
import { fetchTeamsLite } from "../utils/teamsLite";
import {
    AUSTAUSCH_FILTER_LABEL,
    BETEILIGT_ROLLEN_OPTIONEN,
    COL_AUSTAUSCH,
    COL_AUSTAUSCH_EXTERN,
    COL_AUSTAUSCH_MIT,
    buildFeedFilterParams,
    zaehlbareFilter,
} from "../utils/vorgangFeedFilters";
import {
    BetreffAustauschFilter,
    istBetreffAustauschAktiv,
    type BetreffAustauschWerte,
} from "./vorgang/shared/BetreffAustauschFilter";
import { useOhneFilterProbe } from "../hooks/useOhneFilterProbe";
import { OhneFilterHinweis } from "./redesign/table/OhneFilterHinweis";
import { useTableNavState } from "../hooks/useTableNavState";
import {
    getCurrentUserPreferenceKey,
    fetchUserPreferences,
    saveUserPreference,
    VORGAENGE_TABLE_VIEW_SCOPE,
} from "../utils/userPreferences";
import {
    ALLE_STATUS,
    defaultVorgaengeView,
    fromStoredPrefs,
    mitStandardStatus,
    STANDARD_STATUS_FILTER,
    mergeColumnOrder,
    normalizeVorgaengeFilters,
    normalizeVorgaengeView,
    prefsBarVisible,
    toStoredView,
    viewKey,
    viewsDiffer,
    type VorgaengeFilters,
    type VorgaengeTableView,
} from "../utils/vorgaengeTableView";
import { RdContextMenu, type RdContextMenuPosition } from "./redesign/table/RdContextMenu";
import { formatRelativeDay } from "../utils/relativeDay";
import "./redesign/contactRedesign.css";
// I1/E07: liefert `.vgd-persoenlich-badge` für die Kontakt-Spalte.
import "./vorgang/detail/vorgangDetail.css";
import { twvPatch } from "./vorgang/shared/fristZurTwv";


// Statusfilter: Offen/Erledigt untereinander ODER, „Importiert" bzw. „ohne
// importierte" (T-244) per UND dazu (T-196) — Konstanten und Regel in `vorgang/shared/statusFilter.ts`.

/** Höchstens so viele Vorgänge je Sammelaktion (wie beim Kontakt-Mehrfachlöschen). */
const SAMMEL_MAX = 200;
/** „1 Vorgang" / „5 Vorgänge" — für die Rückfragen der Sammelaktionen. */
const anzahlVorgaenge = (n: number): string => (n === 1 ? "1 Vorgang" : `${n} Vorgänge`);

/** Serverseitig sortierbare Spalten — Ids müssen der Backend-Whitelist entsprechen. */
const SORTABLE_COLUMNS = new Set(["Status", "Betreff", "Kontakt", "Frist", "TWV", "Erstellt"]);

/**
 * Trägt diese Vorgangszeile To-dos? Kinder ODER Zähler — der Feed liefert
 * beides, und ein Vorgang mit nur erledigten To-dos darf nicht als „schlank"
 * durchgehen: Sonst verschmölze der Zug ihn flach (siehe `zugWeg`).
 */
const hatTodos = (r: UnifiedRow): boolean => (r.children?.length ?? 0) > 0 || (r.gesamt ?? 0) > 0;

/**
 * Über die Navigation erhaltener Tabellen-Zustand (Detail-Rücksprung). Enthält
 * die serverseitigen Filter, Scope, Suche, Sortierung und die (ggf. noch
 * ungespeicherte) Tabellenansicht sowie Scroll-Position + geladene Zeilenanzahl,
 * um die Ansicht 1:1 wiederherzustellen.
 */
type VorgaengeSnapshot = {
    scope: ScopeKey;
    search: string;
    filterValues: Record<string, string | boolean | undefined>;
    dateRangeValues: Record<string, DateRangeValue | undefined>;
    peopleValues: Record<string, string[] | undefined>;
    boFoValue: { boTeamIds: string[]; foUserIds: string[] };
    // `vorfilter14` („Nächste 14 Tage") ist seit 01.11.2026 entfallen (PO);
    // ein älterer Snapshot trägt das Feld noch, es wird nicht mehr gelesen.
    /** Spalten-Sortierung (serverseitig) — `null` = Standard „neueste zuerst". */
    sort: SortState | null;
    /**
     * Ansicht (Reihenfolge/Sichtbarkeit/Breiten/Eingeklappt) — bewusst MIT im
     * Snapshot, damit ein Detail-Rücksprung auch noch nicht als Standard
     * gespeicherte Spaltenänderungen erhält. Persistiert wird sie davon getrennt
     * (nur auf „Als Standard speichern").
     *
     * OPTIONAL und nur gesetzt, wenn der gespeicherte Standard bereits geladen
     * war: solange die Präferenzen noch unterwegs sind, steht in `view` die
     * Werkseinstellung. Würde die mitgeschrieben, hätte sie beim Rücksprung über
     * `if (!restored?.view)` Vorrang vor dem echten Standard — der Nutzer verlöre
     * seine gespeicherte Ansicht, nur weil er schnell geklickt hat.
     */
    view?: VorgaengeTableView;
    /** Aufgeklappte Vorgänge (Row-Keys) — sonst fallen sie beim Rücksprung zu. */
    expanded: string[];
    scrollTop: number;
    loadedCount: number;
};

/** Trichter-Icon für den mobilen „Filter"-Button (wie ContactTableRedesign). */
const FunnelIcon = (
    <svg width="16" height="16" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
        <path d="M2.5 4h13l-5 6v4l-3 1.5V10z" />
    </svg>
);

/** Schließen-Kreuz für den Filter-Sheet-Kopf. */
const CloseIcon = (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round">
        <path d="M4.5 4.5l9 9M13.5 4.5l-9 9" />
    </svg>
);

/**
 * Datumsfilter im mobilen Filter-Sheet — dieselben Schnellauswahl-Presets wie im
 * Spalten-Popover der Tabelle (Heute / Morgen / Diese Woche / Nächste Woche),
 * darunter weiterhin der freie Von-/Bis-Bereich. Ein erneuter Klick auf den
 * aktiven Preset hebt ihn wieder auf.
 */
const SheetDateFilter: React.FC<{
    label: string;
    value: DateRangeValue | undefined;
    onChange: (next: DateRangeValue) => void;
}> = ({ label, value, onChange }) => {
    const aktiv = matchingDatePreset(value);
    return (
        <div className="ct-sheet-fld">
            <span className="ct-sheet-lbl">{label}</span>
            <div className="vg-sheet-presets">
                {DATE_PRESETS.map((p) => (
                    <button
                        key={p.key}
                        type="button"
                        className={"tbtn" + (aktiv === p.key ? " primary" : "")}
                        aria-pressed={aktiv === p.key}
                        onClick={() => onChange(aktiv === p.key ? {} : datePresetRange(p.key))}
                    >
                        {p.label}
                    </button>
                ))}
            </div>
            <div className="vg-sheet-range">
                <input
                    type="date"
                    className="inp"
                    aria-label={`${label} von`}
                    value={value?.von ?? ""}
                    onChange={(e) => onChange({ von: e.target.value || undefined, bis: value?.bis })}
                />
                <span className="vg-sheet-dash">–</span>
                <input
                    type="date"
                    className="inp"
                    aria-label={`${label} bis`}
                    value={value?.bis ?? ""}
                    onChange={(e) => onChange({ von: value?.von, bis: e.target.value || undefined })}
                />
            </div>
        </div>
    );
};

/**
 * Vorgänge als Redesign-DataTable (SP2): weißer Header, kompakte Dichte,
 * Status+Betreff eingefroren, aufklappbare Kind-To-do-Zeilen, Beteiligte-Popup,
 * relative Frist/TWV, Infinite-Scroll (Seite 30). „Es gibt nur Vorgänge"
 * (F1/A3): eigenständige To-do-Zeilen erscheinen NICHT mehr — To-dos sind
 * ausschließlich aufklappbare Unterzeilen ihres Vorgangs. Mobil bleibt das
 * einfache Karten-Deck.
 */
export const VorgaengeMo: React.FC = () => {
    const navigate = useNavigate();
    const { openWindow } = useWindowManager();
    const { user, roles, teams, isAdmin, isGfgl } = useAuth();
    const currentUserId: string | null = user?.id ?? null;
    const role = roles?.[0] ?? null;
    const hasTeam = (teams?.length ?? 0) > 0;

    const { isPhone } = useResponsive();
    const effectiveView: "tabelle" | "karten" = isPhone ? "karten" : "tabelle";

    // Filter/Scope/Suche/Scroll über die Navigation erhalten — ABER NUR beim
    // Rücksprung aus einer Detailansicht (Vorgang/Todo). Frischer Einstieg in die
    // Liste setzt zurück. Muster + Infrastruktur wie ContactTableRedesign
    // (useTableNavState + useInfiniteRows.initialOffset; `trackTableRoute` ist
    // über NavigationLayout bereits verdrahtet).
    const { initial: restored, save: saveNavState } = useTableNavState<VorgaengeSnapshot>("vorgaenge", {
        // `/vorgaenge/mails` stand hier mit, bis die Seite am 21.09.2026 entfiel.
        isDetailRoute: (p) => /^\/vorgaenge\/\d+/.test(p) || /^\/todos\/\d+/.test(p),
    });

    // Default-Scope je Rolle (A18: admin→Alle, sonst→Team; ohne Team→Persönlich).
    // Ein wiederhergestellter Scope (Detail-Rücksprung) hat Vorrang.
    const [scope, setScope] = useState<ScopeKey>(() => restored?.scope ?? defaultVorgangScope(role, hasTeam));

    const [users, setUsers] = useState<AppUser[]>([]);
    const [reloadKey, setReloadKey] = useState(0);
    const [search, setSearch] = useState(restored?.search ?? "");
    const [searchQuery, setSearchQuery] = useState(restored?.search ?? "");
    // Ausgeklappte Vorgänge (Row-Keys „vorgang-<id>").
    const [expanded, setExpanded] = useState<Set<string>>(() => new Set(restored?.expanded ?? []));
    const [papierkorbOpen, setPapierkorbOpen] = useState(false);
    // Den Vorfilter „Nächste 14 Tage" (beim Einstieg von selbst an, Knopf über
    // der Tabelle bzw. Chip „14 Tage" mobil) gibt es seit 01.11.2026 nicht mehr
    // (PO). Die Liste startet mit dem Statusfilter „Offen" und sonst ungefiltert;
    // der Server kennt den Cluster `naechste14Tage` weiter, er wird nur nicht
    // mehr geschickt.

    // Spaltenfilter (SP3: serverseitig — Werte fließen als Feed-Query-Params).
    // T-156: ohne gesetzten Statusfilter gilt „Offen" (`mitStandardStatus`).
    const [filterValues, setFilterValues] = useState<Record<string, string | boolean | undefined>>(() =>
        mitStandardStatus(restored?.filterValues ?? {}),
    );
    const [dateRangeValues, setDateRangeValues] = useState<Record<string, DateRangeValue | undefined>>(restored?.dateRangeValues ?? {});
    const [peopleValues, setPeopleValues] = useState<Record<string, string[] | undefined>>(restored?.peopleValues ?? {});
    // K-Verantwortlichkeit-Filter (BO-Team + FO-User, wie Kontakttabelle).
    const [boFoValue, setBoFoValue] = useState<{ boTeamIds: string[]; foUserIds: string[] }>(restored?.boFoValue ?? { boTeamIds: [], foUserIds: [] });
    const [teamOptions, setTeamOptions] = useState<BoFoOption[]>([]);
    const [userOptions, setUserOptions] = useState<BoFoOption[]>([]);
    // Mobiles Filter-Sheet (SP4): Bottom-Sheet mit denselben serverseitigen Filtern.
    const [filterSheetOpen, setFilterSheetOpen] = useState(false);

    // Sammelaktion der Fußleiste, die auf die Rückfrage wartet (T-196):
    // erledigen, wieder öffnen oder löschen — immer im eigenen kvp-rd-Dialog.
    const [sammelFrage, setSammelFrage] = useState<"Erledigt" | "Offen" | "loeschen" | null>(null);
    const [sammelBusy, setSammelBusy] = useState(false);
    /**
     * Ein per Maus auf einen anderen gezogener Vorgang, der auf die Rückfrage
     * wartet (PO 21.09.2026). Erst nach „Verschmelzen" passiert etwas.
     */
    const [pendingMerge, setPendingMerge] = useState<{ quelle: VorgangZug; ziel: VorgangZug } | null>(
        null,
    );
    const [mergeBusy, setMergeBusy] = useState(false);
    /**
     * Ein per Maus auf einen fremden Vorgang gezogenes TO-DO, das auf die
     * Rückfrage wartet (PO 22.09.2026). `quelleTitel` steht mit im Zustand,
     * weil die Erklärung sagen muss, WO es danach fehlt — und die Zeile, aus
     * der es kam, ist beim Loslassen nicht mehr greifbar.
     */
    const [pendingTodoZug, setPendingTodoZug] = useState<{
        todo: TodoZug;
        quelleTitel: string;
        ziel: VorgangZug;
    } | null>(null);
    const [todoZugBusy, setTodoZugBusy] = useState(false);
    /** Angehakte VORGÄNGE (nie To-dos) für „Duplikate zusammenführen". */
    const [gewaehlteIds, setGewaehlteIds] = useState<Set<number>>(new Set());
    const [dupOffen, setDupOffen] = useState(false);
    /**
     * Was gerade gezogen wird — als Ref, nicht als State: Ein `setState` bei
     * `dragstart` löst ein Neuzeichnen mitten im Zug aus, und Chrome bricht
     * ihn dann ab. Gebraucht wird der Wert ohnehin nur in Handlern.
     *
     * `id` bedeutet je nach Art etwas anderes: beim Vorgang seine eigene Id,
     * beim To-do die Id seines HEIMAT-Vorgangs. Beide beantworten dieselbe
     * Frage — auf welche Zeile darf nicht losgelassen werden.
     */
    const gezogenRef = useRef<{ art: "vorgang" | "todo"; id: number; todoId?: number } | null>(null);
    /**
     * Ein To-do auf ein GESCHWISTER gezogen (PO 28.09.2026: „zusammenführen
     * der To-dos über Drag and Drop"). Öffnet den Zusammenführen-Dialog der
     * Detailansicht, mit dem getroffenen To-do als Ziel.
     */
    const [todoMerge, setTodoMerge] = useState<{ vorgangId: number; quelleId: number; zielId: number } | null>(
        null,
    );
    const [toast, setToast] = useState<{ message: string; tone: "error" | "success" | "info" } | null>(null);

    // Spalten-Sortierung (serverseitig). `null` = Standard „neueste zuerst".
    // Eine wiederhergestellte Sortierung wird gegen die Whitelist geprüft — sonst
    // könnte ein alter Snapshot auf eine inzwischen entfernte Spalte zeigen und
    // der Server würde sie stillschweigend ignorieren, während der Pfeil im Kopf
    // eine Sortierung behauptet.
    const [sort, setSort] = useState<SortState | null>(() => {
        const s = restored?.sort;
        return s && SORTABLE_COLUMNS.has(s.field) ? s : null;
    });

    /**
     * Tabellenansicht (Reihenfolge / Sichtbarkeit / Breiten / eingeklappt).
     * `view` ist der ARBEITSSTAND — Änderungen wirken sofort und überleben den
     * Detail-Rücksprung; `savedView` ist der als Standard gespeicherte Stand.
     * Weichen beide voneinander ab, erscheint unten die Speichern-Leiste. Ein
     * Reload ohne Speichern fällt bewusst auf `savedView` zurück.
     */
    const [view, setView] = useState<VorgaengeTableView>(() =>
        restored?.view ? normalizeVorgaengeView(restored.view) : defaultVorgaengeView(),
    );
    const [savedView, setSavedView] = useState<VorgaengeTableView>(defaultVorgaengeView);
    const [viewSaving, setViewSaving] = useState(false);
    /**
     * Speichern-Leiste: sie erscheint bei einer geänderten SPALTENANSICHT und
     * auf ausdrücklichen Wunsch (Rechtsklick → „Tabellen-Präferenzen"). Filter
     * und Bereich lösen sie BEWUSST nicht aus — die ändert man ständig, und eine
     * Leiste, die dabei jedes Mal nach einer globalen Einstellung fragt, wäre
     * nur Lärm. Gespeichert werden sie trotzdem mit, sobald jemand speichert.
     */
    const [prefsRequested, setPrefsRequested] = useState(false);
    /** Position des Rechtsklick-Menüs auf der Bereichsauswahl (Persönlich/Team/Alle). */
    const [scopeMenu, setScopeMenu] = useState<RdContextMenuPosition | null>(null);
    /**
     * Der Ansichts-Stand, bei dem zuletzt „Verwerfen" gedrückt wurde. Verwerfen
     * nimmt die Änderung NICHT zurück (die Ansicht bleibt, wie sie ist) — es
     * schließt nur die Leiste. Ohne diesen Merker käme sie sofort wieder, weil
     * die Ansicht ja weiterhin vom Standard abweicht.
     */
    const [dismissedViewKey, setDismissedViewKey] = useState<string | null>(null);
    const columnOrder = view.order;
    const columnVisibility = view.visibility;
    const columnWidths = view.widths;
    const collapsedColumns = view.collapsed;

    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const id = setTimeout(() => setSearchQuery(search.trim()), 300);
        return () => clearTimeout(id);
    }, [search]);

    // „Team" nur anbieten, wenn der Benutzer mind. einem Team angehört.
    const scopeOptions = useMemo(
        () => bereichOptionen(hasTeam),
        [hasTeam],
    );
    // Ein wiederhergestellter Scope gilt als „vom User gesetzt" → der Rollen-
    // Default-Effekt darf ihn nicht überschreiben.
    const scopeTouchedRef = useRef(!!restored);
    const onScopeChange = useCallback((key: ScopeKey) => {
        scopeTouchedRef.current = true;
        setScope(key);
    }, []);
    useEffect(() => {
        if (scopeTouchedRef.current || !role) return;
        setScope(defaultVorgangScope(role, hasTeam));
    }, [role, hasTeam]);
    useEffect(() => {
        if (!hasTeam && scope === "team") setScope("persoenlich");
    }, [hasTeam, scope]);

    // Serverseitige Spaltenfilter (SP3): UI-Zustand → Feed-Query-Params. Der
    // JSON-Key steuert den Refetch (useInfiniteRows-deps). Ohne Status-Filter
    // liefert der Server OFFENE UND ERLEDIGTE, erledigte zuletzt (PO 02.08.2026);
    // seit T-156 steht der Filter aber von Haus aus auf „Offen", und „Alle"
    // ist ausdrücklich „Offen,Erledigt" (`STANDARD_STATUS_FILTER`, `ALLE_STATUS`).
    const feedFilters = useMemo(
        () => buildFeedFilterParams({ filterValues, dateRangeValues, peopleValues, boFoValue }),
        [filterValues, dateRangeValues, peopleValues, boFoValue],
    );
    const feedFiltersKey = useMemo(() => JSON.stringify(feedFilters), [feedFilters]);
    const feedFiltersRef = useRef(feedFilters);
    feedFiltersRef.current = feedFilters;
    // Sortierung geht serverseitig (über ALLE Seiten, nicht nur die geladenen) —
    // deshalb im Refetch-Key und per Ref an den Fetch, wie die Spaltenfilter.
    const sortKey = sort ? `${sort.field}:${sort.direction}` : "";
    const sortRef = useRef(sort);
    sortRef.current = sort;

    /**
     * Sind die gespeicherten Einstellungen (Bereich, Filter, Ansicht) geladen?
     * Gesetzt vom Präferenz-Effekt weiter unten — auch bei Fehler, dann gilt
     * die Werkseinstellung.
     */
    const [viewHydrated, setViewHydrated] = useState(false);
    /**
     * T-400 (Dirk, 26.10.2026): Die Liste lud beim frischen Einstieg sofort mit
     * dem Rollen-Standard und gleich danach noch einmal mit dem gespeicherten
     * Bereich/Filter — sie wurde unter dem Finger ausgetauscht, ein Tipp in
     * dieser Phase öffnete nicht den gewünschten Vorgang. Jetzt lädt sie erst,
     * wenn die gespeicherten Einstellungen da sind (bis dahin „Lade Vorgänge …“).
     * Beim Detail-Rücksprung ändern die Einstellungen nichts mehr am Feed (der
     * zurückgeholte Stand hat Vorrang) — dort lädt sie sofort.
     */
    const feedBereit = viewHydrated || !!restored;

    const feed = useInfiniteRows<VorgangFeedItem>({
        fetchPage: ({ limit, offset, signal }) =>
            vorgangService
                .getFeedPage(
                    scope,
                    offset,
                    limit,
                    signal,
                    undefined,
                    searchQuery,
                    feedFiltersRef.current,
                    sortRef.current,
                )
                .then(({ items, total }) => ({ rows: items, total })),
        getKey: (it) => `${it.kind}-${it.vorgang?.id ?? it.todo?.id}`,
        deps: [scope, searchQuery, feedFiltersKey, reloadKey, sortKey],
        // Beim Detail-Rücksprung die zuvor geladene Zeilenanzahl in EINEM Request
        // laden (gedeckelt), damit die Scroll-Position wieder erreichbar ist.
        initialOffset: restored ? Math.min(restored.loadedCount, 300) : undefined,
        enabled: feedBereit,
    });
    /** Lädt gerade — oder wartet noch auf die gespeicherten Einstellungen. */
    const feedLaedt = feed.loading || !feedBereit;
    const reloadFeed = useCallback(() => setReloadKey((k) => k + 1), []);

    // ---- Navigations-Zustand sichern + Scroll wiederherstellen ----
    // Snapshot beim Scrollen (rAF-gedrosselt) und beim Verlassen (→ Detail)
    // sichern; beim Detail-Rücksprung stellt useLayoutEffect die Scroll-Position
    // wieder her, sobald genug Zeilen geladen sind. Muster wie ContactTableRedesign.
    // Der Initialwert lässt die Ansicht bewusst offen — sie kommt erst weiter
    // unten dazu, sobald der gespeicherte Standard geladen ist.
    const latestSnapRef = useRef<Omit<VorgaengeSnapshot, "scrollTop">>({
        scope, search, filterValues, dateRangeValues, peopleValues, boFoValue,
        sort, expanded: [...expanded],
        loadedCount: feed.rows.length,
    });
    const persistNav = useCallback(() => {
        saveNavState({ ...latestSnapRef.current, scrollTop: scrollRef.current?.scrollTop ?? 0 });
    }, [saveNavState]);
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        let raf = 0;
        const onScroll = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(persistNav);
        };
        el.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            el.removeEventListener("scroll", onScroll);
            cancelAnimationFrame(raf);
        };
    }, [persistNav]);
    useEffect(() => () => persistNav(), [persistNav]); // beim Verlassen (→ Detail) sichern
    // Scroll-Position einmalig wiederherstellen, sobald genug Zeilen geladen sind.
    const restoredScrollRef = useRef(false);
    useLayoutEffect(() => {
        if (restoredScrollRef.current) return;
        if (!restored) {
            restoredScrollRef.current = true;
            return;
        }
        if (feed.rows.length >= Math.min(restored.loadedCount, 300)) {
            if (scrollRef.current) scrollRef.current.scrollTop = restored.scrollTop;
            restoredScrollRef.current = true;
        }
    }, [feed.rows.length, restored]);

    useEffect(() => {
        listAppUsers().then(setUsers).catch(() => undefined);
    }, []);

    // BO-Teams + FO-User für den K-Verantwortlichkeit-Filter (boFo), Muster wie
    // ContactTableRedesign: Teams aus rbac/teams-lite, FO = department enthält "FO".
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const teams = await fetchTeamsLite();
                if (alive) {
                    setTeamOptions(
                        teams
                            .map((t: { id: string; name?: string }) => ({ id: String(t.id), label: String(t.name ?? "").replace(/^Team\s+/i, "") }))
                            .filter((o) => o.id && o.label),
                    );
                }
            } catch {
                /* Optionen bleiben leer */
            }
            try {
                const list = await listAppUsers();
                if (alive) {
                    setUserOptions(
                        list
                            .filter((u) => (u.department ?? "").split("+").includes("FO") && (u.role ?? "").toLowerCase() !== "admin")
                            .map((u) => ({ id: String(u.id), label: userDisplay(u) }))
                            .filter((o) => o.id && o.label),
                    );
                }
            } catch {
                /* Optionen bleiben leer */
            }
        })();
        return () => {
            alive = false;
        };
    }, []);

    // ─── Gespeicherte Präferenzen laden (Ansicht + Bereich + Filter) ───
    // Nur LADEN — geschrieben wird ausschließlich auf ausdrückliches Speichern
    // (siehe savePrefs). Das frühere Auto-Save schrieb jede Maus-Geste sofort
    // weg; wer eine Spalte nur kurz zur Seite schob, hatte das dauerhaft.
    const prefKeyRef = useRef<string | null>(null);
    useEffect(() => {
        let alive = true;
        (async () => {
            const key = await getCurrentUserPreferenceKey();
            if (!alive) return;
            prefKeyRef.current = key;
            if (key) {
                try {
                    const prefs = await fetchUserPreferences([VORGAENGE_TABLE_VIEW_SCOPE]);
                    if (!alive) return;
                    const stored = fromStoredPrefs(prefs[VORGAENGE_TABLE_VIEW_SCOPE]);
                    setSavedView(stored.view);
                    // Ein über die Navigation zurückgeholter Arbeitsstand hat Vorrang
                    // (der Detail-Rücksprung soll exakt die verlassene Ansicht zeigen).
                    if (!restored?.view) setView(stored.view);
                    if (!restored) {
                        // Bereich: gilt als vom Nutzer gesetzt, damit der
                        // Rollen-Default-Effekt ihn nicht gleich überschreibt.
                        if (stored.scope) {
                            scopeTouchedRef.current = true;
                            setScope(stored.scope);
                        }
                        if (stored.filters) {
                            // Vor T-156 gespeicherte Ansichten haben keinen
                            // Statusfilter — für sie gilt die neue Werkseinstellung.
                            setFilterValues(mitStandardStatus(stored.filters.filterValues));
                            setDateRangeValues(stored.filters.dateRangeValues);
                            setPeopleValues(stored.filters.peopleValues);
                            setBoFoValue(stored.filters.boFoValue);
                            setSort(
                                stored.filters.sort && SORTABLE_COLUMNS.has(stored.filters.sort.field)
                                    ? stored.filters.sort
                                    : null,
                            );
                        }
                    }
                } catch {
                    /* Werkseinstellung beibehalten */
                }
            }
            if (alive) setViewHydrated(true);
        })();
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Weicht die SPALTENANSICHT vom gespeicherten Standard ab? Nur das öffnet
     * die Leiste von selbst — Filter und Bereich bewusst nicht.
     */
    const viewDirty = viewHydrated && viewsDiffer(view, savedView);
    const currentViewKey = viewKey(view);
    /** Sichtbar bei geänderter Ansicht (sofern nicht verworfen) oder auf Wunsch. */
    const prefsBarOpen = prefsBarVisible(prefsRequested, viewDirty, currentViewKey, dismissedViewKey);

    // Navigations-Snapshot bei jedem Render nachziehen — bewusst HIER und nicht
    // oben bei `latestSnapRef`: erst ab dieser Zeile ist `viewHydrated` bekannt,
    // und die Ansicht darf nur in den Snapshot, wenn der gespeicherte Standard
    // schon geladen ist (siehe Kommentar an `VorgaengeSnapshot.view`). Die
    // Zuweisung im Render-Body genügt, weil `persistNav` den Ref erst in einem
    // Effekt/Callback liest, also immer nach dem Render.
    latestSnapRef.current = {
        scope, search, filterValues, dateRangeValues, peopleValues, boFoValue,
        sort, view: viewHydrated ? view : undefined, expanded: [...expanded],
        loadedCount: feed.rows.length,
    };

    /** Der aktuelle Filterzustand in Speicherform (ohne Suchtext — der ist flüchtig). */
    const currentFilters = useMemo<VorgaengeFilters>(
        () =>
            normalizeVorgaengeFilters({
                filterValues: filterValues as Record<string, string | boolean>,
                dateRangeValues: dateRangeValues as Record<string, { von?: string; bis?: string }>,
                peopleValues: peopleValues as Record<string, string[]>,
                boFoValue,
                sort,
            }),
        [filterValues, dateRangeValues, peopleValues, boFoValue, sort],
    );

    /**
     * Schreibt den GESAMTEN Arbeitsstand als Standard: Spaltenansicht,
     * Sichtbarkeitsbereich und Filter. Bewusst alles auf einmal — wer speichert,
     * meint die Liste, wie sie gerade vor ihm liegt, und nicht einen Ausschnitt
     * davon.
     */
    const savePrefs = useCallback(async () => {
        const key = prefKeyRef.current;
        const next = normalizeVorgaengeView(view);
        if (!key) {
            setToast({ message: "Einstellung konnte nicht gespeichert werden — keine Sitzung.", tone: "error" });
            return;
        }
        setViewSaving(true);
        try {
            await saveUserPreference(
                key,
                VORGAENGE_TABLE_VIEW_SCOPE,
                toStoredView({ view: next, scope, filters: currentFilters }),
            );
            setSavedView(next);
            setPrefsRequested(false);
            setDismissedViewKey(null);
            setToast({ message: "Ansicht, Bereich und Filter als Standard gespeichert.", tone: "success" });
        } catch {
            setToast({ message: "Einstellung konnte nicht gespeichert werden.", tone: "error" });
        } finally {
            setViewSaving(false);
        }
    }, [view, scope, currentFilters]);

    // ─── Ausklappen (via Ref, damit der Spalten-Memo stabil bleibt) ───
    const expandedRef = useRef(expanded);
    expandedRef.current = expanded;
    const toggleExpand = useCallback((key: string) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    // ─── Feed → Zeilen ───
    // „Es gibt nur Vorgänge" (F1/A3): der Feed liefert ausschließlich Vorgänge;
    // eigenständige To-do-Zeilen werden nicht mehr gerendert. Kind-To-dos hängen
    // als aufklappbare Unterzeilen an ihrem Vorgang (v.todos → children).
    const vorgaenge = useMemo(
        () => feed.rows.filter((it) => it.kind === "vorgang" && it.vorgang).map((it) => it.vorgang as VorgangDto),
        [feed.rows],
    );

    const rows: UnifiedRow[] = useMemo(
        () => baueVorgangZeilen(vorgaenge, { users, currentUserId, isAdmin, isGfgl, teams }),
        [vorgaenge, users, currentUserId, isAdmin, isGfgl, teams],
    );

    // ─── Handler ───
    const updateFilter = useCallback((columnId: string, value: string | boolean | undefined) => {
        setFilterValues((prev) => {
            const next = { ...prev };
            // Status geleert = alle Status, und zwar ausdrücklich: ein fehlender
            // Statusfilter fiele beim Rücksprung aus dem Detail wieder auf
            // „Offen" zurück (T-156, `mitStandardStatus`).
            if (columnId === "Status" && (value === undefined || value === "")) next[columnId] = ALLE_STATUS;
            else if (value === undefined || value === "" || value === false) delete next[columnId];
            else next[columnId] = value;
            return next;
        });
    }, []);
    const updateDateRange = useCallback((columnId: string, next: DateRangeValue) => {
        setDateRangeValues((prev) => {
            const copy = { ...prev };
            if (!next.von && !next.bis) delete copy[columnId];
            else copy[columnId] = next;
            return copy;
        });
    }, []);
    const updatePeople = useCallback((columnId: string, next: string[]) => {
        setPeopleValues((prev) => {
            const copy = { ...prev };
            if (!next.length) delete copy[columnId];
            else copy[columnId] = next;
            return copy;
        });
    }, []);
    // ─── Ansicht ändern (wirkt sofort, persistiert erst per Speichern-Leiste) ───
    // Die DataTable meldet beim Verschieben NUR die sichtbaren Spalten zurück —
    // mergeColumnOrder setzt die ausgeblendeten an ihrer alten Nachbarschaft
    // wieder ein, sonst fielen sie aus der Reihenfolge.
    const setColumnOrder = useCallback(
        (nextVisible: string[]) => setView((v) => ({ ...v, order: mergeColumnOrder(v.order, nextVisible) })),
        [],
    );
    const setColumnVisibility = useCallback(
        (next: Record<string, boolean>) => setView((v) => normalizeVorgaengeView({ ...v, visibility: next })),
        [],
    );
    const setColumnWidths = useCallback((next: Record<string, number>) => setView((v) => ({ ...v, widths: next })), []);
    const toggleCollapsed = useCallback(
        (id: string) =>
            setView((v) => ({
                ...v,
                collapsed: v.collapsed.includes(id) ? v.collapsed.filter((x) => x !== id) : [...v.collapsed, id],
            })),
        [],
    );
    /**
     * „Verwerfen" schließt NUR die Leiste — die Ansicht bleibt, wie sie ist.
     *
     * Vorher setzte der Knopf den Arbeitsstand auf den gespeicherten Standard
     * zurück und machte damit die gerade vorgenommene Spaltenänderung zunichte.
     * Wer die Leiste bloß loswerden wollte, verlor seine Arbeit; wer wirklich
     * zurück will, hat dafür „Werkseinstellung" bzw. lädt neu.
     */
    const dismissPrefsBar = useCallback(() => {
        setPrefsRequested(false);
        setDismissedViewKey(currentViewKey);
    }, [currentViewKey]);
    /** Auf die Werkseinstellung zurück (muss danach noch gespeichert werden). */
    const resetViewToFactory = useCallback(() => {
        setView(defaultVorgaengeView());
        setDismissedViewKey(null);
    }, []);

    /** Nur Vorgangszeilen sind auswählbar — To-do-Kinder zählen nie mit. */
    const waehlbareIds = useMemo(
        () => rows.filter((r) => r.kind === "vorgang").map((r) => r.id),
        [rows],
    );
    const alleGewaehlt =
        waehlbareIds.length > 0 && waehlbareIds.every((id) => gewaehlteIds.has(id));

    const auswahlUmschalten = useCallback((id: number) => {
        setGewaehlteIds((alt) => {
            const neu = new Set(alt);
            if (neu.has(id)) neu.delete(id);
            else neu.add(id);
            return neu;
        });
    }, []);

    const alleUmschalten = useCallback(() => {
        setGewaehlteIds((alt) =>
            waehlbareIds.length > 0 && waehlbareIds.every((id) => alt.has(id))
                ? new Set()
                : new Set(waehlbareIds),
        );
    }, [waehlbareIds]);

    /**
     * Die angehakten Vorgänge als Kandidaten für den Dialog. Er braucht
     * `createdAt` für die Vorauswahl „der Älteste bleibt" — er trägt die
     * Nummer, die schon im Umlauf ist.
     */
    const dupKandidaten = useMemo(
        () =>
            rows
                .filter((r) => r.kind === "vorgang" && gewaehlteIds.has(r.id))
                // Ohne Vorgangsnummer: `UnifiedRow` trägt sie nicht, und für
                // die Unterscheidung im Dialog reicht der Betreff.
                .map((r) => ({ id: r.id, titel: r.titel ?? "", createdAt: r.createdAt ?? null })),
        [rows, gewaehlteIds],
    );

    /** Mehr als {@link SAMMEL_MAX} angehakt? Dann sperrt die Fußleiste die Sammelaktionen. */
    const zuVieleGewaehlt = gewaehlteIds.size > SAMMEL_MAX;
    const zuVieleText = `Höchstens ${SAMMEL_MAX} Vorgänge auf einmal — bitte weniger auswählen.`;
    /** Darf der User mindestens einen der angehakten Vorgänge löschen? (Server prüft je Vorgang.) */
    const darfAuswahlLoeschen = useMemo(
        () => rows.some((r) => r.kind === "vorgang" && gewaehlteIds.has(r.id) && r.canDelete),
        [rows, gewaehlteIds],
    );

    /**
     * Die Liste nach einem Verschmelzen nachziehen — OHNE Neuladen
     * (PO 21.09.2026: „das verschmelzen muss in echtzeit angezeigt werden,
     * die veränderung nicht erst durch reload").
     *
     * Die Quellzeilen verschwinden sofort; das Ziel bekommt die Felder frisch,
     * die der Server dabei ändert (To-dos, Fortschritt, Status). Warum nicht
     * `reloadFeed()`: Das wirft alle nachgeladenen Seiten weg — wer weit unten
     * arbeitet, findet das Ergebnis seiner eigenen Handlung danach nicht mehr.
     * Begründung ausführlich in `verschmelzAnzeige.ts`.
     *
     * Der Nachschlag darf scheitern, ohne dass etwas kaputtgeht: Die Quelle
     * ist dann trotzdem fort (sie IST ja verschmolzen), nur der Fortschritt am
     * Ziel hinkt bis zum nächsten Laden.
     */
    const zieheVerschmelzungNach = useCallback(
        async (
            zielId: number,
            quellIds: number[],
            /**
             * Quellvorgänge, die STEHEN BLEIBEN, sich aber verändert haben —
             * seit dem 22.09.2026 möglich: Wer nur einzelne To-dos übernimmt,
             * lässt den Rest zurück. Ihre Zeilen zeigen sonst weiter To-dos,
             * die längst drüben sind.
             */
            veraenderte: number[] = [],
        ) => {
            feed.removeByKeys(quellIds.map((id) => `vorgang-${id}`));
            // Eine verschmolzene Quelle darf nicht angehakt bleiben: Sonst
            // zählt die Fußleiste eine Auswahl, zu der es keine Zeile mehr
            // gibt — und „Zusammenführen" böte einen Vorgang an, der im
            // Papierkorb liegt. Trifft den Zieh-Weg; der Dialog leert die
            // Auswahl ohnehin ganz.
            setGewaehlteIds((alt) => {
                if (!quellIds.some((id) => alt.has(id))) return alt;
                const neu = new Set(alt);
                for (const id of quellIds) neu.delete(id);
                return neu;
            });
            // Das Ziel und alles, was sich sonst verändert hat — dieselbe
            // Behandlung, denn beide Male gilt: nur zeigen, was ein Neuladen
            // auch zeigte (siehe `verschmelzAnzeige.ts`).
            for (const id of [zielId, ...veraenderte]) {
                try {
                    const frisch = await vorgangService.getVorgang(id, { force: true });
                    feed.patchByKey(`vorgang-${id}`, (it) =>
                        it.vorgang ? { ...it, vorgang: zielNachVerschmelzen(it.vorgang, frisch) } : it,
                    );
                } catch {
                    // Bewusst still: Die sichtbare Veränderung — die Quelle ist
                    // fort bzw. leichter — hat bereits stattgefunden. Ein
                    // Fehlerton dafür wäre irreführend; die Handlung selbst hat
                    // geklappt, nur die Zahlen hinken bis zum nächsten Laden.
                }
            }
        },
        [feed.removeByKeys, feed.patchByKey],
    );

    /** Der Betreff eines Vorgangs aus den geladenen Zeilen — für die Rückfrage. */
    const titelVonVorgang = useCallback(
        (id: number | null) =>
            id == null
                ? "(ohne Betreff)"
                : rows.find((r) => r.kind === "vorgang" && r.id === id)?.titel || "(ohne Betreff)",
        [rows],
    );

    /**
     * Ein gezogenes To-do in einen fremden Vorgang verschieben — nach der
     * Rückfrage (PO 22.09.2026).
     *
     * Beide Seiten ändern sich sichtbar: Beim alten Vorgang fällt ein Kind weg,
     * beim neuen kommt eines dazu. Bis 03.11.2026 wurde das lokal gerechnet;
     * seit der Server ein einzeln übrig gebliebenes To-do in den Vorgang
     * zurückfaltet, ist das Ergebnis nicht mehr vorab bekannt — beide Zeilen
     * werden deshalb einzeln frisch geholt (kein Neuladen der ganzen Liste,
     * siehe `verschmelzAnzeige.ts`).
     */
    const confirmTodoZug = useCallback(async () => {
        if (!pendingTodoZug) return;
        const { todo, ziel } = pendingTodoZug;
        setTodoZugBusy(true);
        try {
            await vorgangService.verschiebeTodo(todo.id, ziel.id);
            setPendingTodoZug(null);

            // BEIDE Seiten frisch nachladen statt lokal rechnen (User
            // 03.11.2026): Der Server faltet ein übrig gebliebenes EINZELNES
            // To-do in seinen Vorgang zurück — beim alten Vorgang, wenn er auf
            // eines fällt, und beim neuen, wenn er vorher leer war. Was danach
            // dasteht, weiß nur er. Derselbe Weg wie beim Verschmelzen (nur
            // diese beiden Zeilen, kein Neuladen der Liste).
            void zieheVerschmelzungNach(ziel.id, [], todo.vorgangId != null ? [todo.vorgangId] : []);

            setToast({
                message: `„${todo.titel}" steht jetzt in „${ziel.titel}".`,
                tone: "success",
            });
        } catch (err: any) {
            // Der Server lehnt fehlendes Schreibrecht am To-do und fehlendes
            // Leserecht am Ziel ab. Beides steht in `message`.
            setToast({ message: err?.message ?? "Verschieben fehlgeschlagen.", tone: "error" });
            reloadFeed();
        } finally {
            setTodoZugBusy(false);
        }
    }, [pendingTodoZug, zieheVerschmelzungNach, reloadFeed]);

    /** Verschmelzen ausführen — nach der Rückfrage (PO 21.09.2026). */
    const confirmMerge = useCallback(async () => {
        if (!pendingMerge) return;
        setMergeBusy(true);
        try {
            // Schlank auf gegliedert wird ein To-do, kein flacher Merge
            // (PO 28.09.2026, `zugWeg`). Sonst verschwand die Quelle im
            // Papierkorb, und ihr Inhalt tauchte im ersten To-do des Ziels
            // unter dessen Betreff unter — „es passiert nix".
            if (zugWeg(pendingMerge.quelle, pendingMerge.ziel) === "alsTodo") {
                await vorgangService.verschiebeVorgangAlsTodo(pendingMerge.quelle.id, pendingMerge.ziel.id);
            } else {
                await vorgangService.verschmelzeVorgaenge(pendingMerge.ziel.id, pendingMerge.quelle.id);
            }
            setPendingMerge(null);
            // Der Nachschlag läuft OHNE `await` weiter. Sonst bliebe `mergeBusy`
            // über eine zweite Netzrunde stehen, und wer in dieser Zeit den
            // nächsten Vorgang zieht, bekäme einen Bestätigungsdialog, der sich
            // nicht schließen lässt: `busy` sperrt beide Knöpfe, Escape und den
            // Klick daneben. Hängt die Antwort, hängt das Fenster. Die
            // Verschmelzung selbst ist an dieser Stelle längst bestätigt.
            void zieheVerschmelzungNach(pendingMerge.ziel.id, [pendingMerge.quelle.id]);
            setToast({
                message:
                    zugWeg(pendingMerge.quelle, pendingMerge.ziel) === "alsTodo"
                        ? `„${pendingMerge.quelle.titel}" ist jetzt ein To-do in „${pendingMerge.ziel.titel}".`
                        : `„${pendingMerge.quelle.titel}" steht jetzt in „${pendingMerge.ziel.titel}".`,
                tone: "success",
            });
        } catch (err: any) {
            // Der Server lehnt zwei Fälle bewusst ab: fehlendes Schreibrecht am
            // Ziel und fehlendes Löschrecht an der Quelle. Beides steht in
            // `message` und ist für den Anwender die eigentliche Auskunft.
            setToast({ message: err?.message ?? "Verschmelzen fehlgeschlagen.", tone: "error" });
        } finally {
            setMergeBusy(false);
        }
    }, [pendingMerge, zieheVerschmelzungNach]);

    /**
     * Die Liste nach einem Status-Wechsel nachziehen — OHNE Neuladen (T-196).
     * Passt eine Zeile danach nicht mehr zum Statusfilter (Liste „offene
     * Importe", jetzt erledigt), verschwindet sie: die Arbeitsliste schrumpft.
     * Sonst bekommt sie den Stand, den ein Neuladen zeigte (`vorgangNachStatus`
     * spiegelt die Kaskade des Servers, inkl. T-218 „Termine leeren").
     */
    const feedRowsRef = useRef(feed.rows);
    feedRowsRef.current = feed.rows;
    const statusFilterRef = useRef<string | undefined>(undefined);
    statusFilterRef.current = filterValues["Status"] as string | undefined;
    const zieheStatusNach = useCallback(
        (ids: number[], status: "Offen" | "Erledigt") => {
            const weg: string[] = [];
            for (const id of ids) {
                const key = `vorgang-${id}`;
                const item = feedRowsRef.current.find((it) => it.kind === "vorgang" && it.vorgang?.id === id);
                if (!item?.vorgang) continue;
                const neu = vorgangNachStatus(item.vorgang, status);
                if (!passtZumStatusFilter(deriveVorgangStatus(neu), neu.importiert === true, statusFilterRef.current)) {
                    weg.push(key);
                } else {
                    feed.patchByKey(key, (it) => (it.vorgang ? { ...it, vorgang: vorgangNachStatus(it.vorgang, status) } : it));
                }
            }
            if (weg.length) feed.removeByKeys(weg);
        },
        [feed.patchByKey, feed.removeByKeys],
    );

    /**
     * Offen/Erledigt direkt in der Zeile (PO 06.10.2026, T-196 — hebt die
     * Linie „Übersicht rein lesend" vom 26.07.2026 auf). Ohne Rückfrage, aber
     * mit Meldung samt Betreff; nur wo der User den Vorgang bearbeiten darf.
     */
    const statusUmschalten = useCallback(
        async (row: UnifiedRow) => {
            if (row.kind !== "vorgang" || !row.canDelete) return;
            const neu: "Offen" | "Erledigt" = row.status === "Erledigt" ? "Offen" : "Erledigt";
            const titel = row.titel || "(ohne Betreff)";
            try {
                await vorgangService.updateVorgang(row.id, { statusManuell: neu });
                zieheStatusNach([row.id], neu);
                setToast({
                    message: neu === "Erledigt" ? `„${titel}“ ist erledigt.` : `„${titel}“ ist wieder offen.`,
                    tone: "success",
                });
            } catch (err: any) {
                setToast({
                    message: String(err?.message ?? "").includes("403")
                        ? "Kein Recht, den Status dieses Vorgangs zu ändern."
                        : "Der Status konnte nicht geändert werden.",
                    tone: "error",
                });
            }
        },
        [zieheStatusNach],
    );

    /** Übersprungene einer Sammelaktion in Worte fassen („2 ohne Berechtigung"). */
    const skipText = (skipped: SammelSkip[]): string => {
        const ohneRecht = skipped.filter((x) => x.reason === "forbidden").length;
        const fehlt = skipped.filter((x) => x.reason === "not_found").length;
        const fehler = skipped.filter((x) => x.reason === "fehler").length;
        const teile: string[] = [];
        if (ohneRecht) teile.push(`${ohneRecht} ohne Berechtigung`);
        if (fehlt) teile.push(`${fehlt} nicht mehr vorhanden`);
        if (fehler) teile.push(`${fehler} mit Fehler`);
        return teile.join(", ");
    };

    /** Sammelaktion der Fußleiste ausführen, nachdem die Rückfrage bestätigt ist. */
    const sammelAusfuehren = useCallback(async () => {
        if (!sammelFrage) return;
        const ids = [...gewaehlteIds];
        if (ids.length === 0 || ids.length > SAMMEL_MAX) {
            setSammelFrage(null);
            return;
        }
        setSammelBusy(true);
        try {
            let erledigt: number[];
            let skipped: SammelSkip[];
            let wort: string;
            if (sammelFrage === "loeschen") {
                const res = await vorgangService.loescheMehrfach(ids);
                erledigt = res.geloescht ?? [];
                skipped = res.skipped ?? [];
                feed.removeByKeys(erledigt.map((id) => `vorgang-${id}`));
                wort = "gelöscht";
            } else {
                const res = await vorgangService.setzeStatusMehrfach(ids, sammelFrage);
                erledigt = res.geaendert ?? [];
                skipped = res.skipped ?? [];
                zieheStatusNach(erledigt, sammelFrage);
                wort = sammelFrage === "Erledigt" ? "erledigt" : "wieder geöffnet";
            }
            // Verarbeitete raus aus der Auswahl; Übersprungene bleiben angehakt,
            // damit man sieht, welche nicht gingen.
            setGewaehlteIds((alt) => {
                const neu = new Set(alt);
                for (const id of erledigt) neu.delete(id);
                return neu;
            });
            const rest = skipText(skipped);
            const anzahl = erledigt.length === 1 ? "1 Vorgang" : `${erledigt.length} Vorgänge`;
            setToast({
                message: rest ? `${anzahl} ${wort}, ${rest}.` : `${anzahl} ${wort}.`,
                tone: rest ? "info" : "success",
            });
        } catch (err: any) {
            setToast({ message: err?.message || "Die Aktion ist fehlgeschlagen.", tone: "error" });
        } finally {
            setSammelBusy(false);
            setSammelFrage(null);
        }
    }, [sammelFrage, gewaehlteIds, feed.removeByKeys, zieheStatusNach]);

    // To-dos haben keine eigene Detailseite mehr — eine To-do-Zeile öffnet den
    // Eltern-Vorgang mit genau DIESEM To-do aufgeklappt und angesprungen
    // (`?todo=`, Sprungmarke #2339), nicht nur den ganzen Vorgang. Der
    // Eltern-Vorgang ist hier immer bekannt (To-do-Zeilen entstehen nur als
    // Kinder eines geladenen Vorgangs); fehlt er wider Erwarten, passiert nichts
    // — besser als eine Navigation auf `/vorgaenge/undefined`.
    const openRow = useCallback(
        (r: UnifiedRow) => {
            const link = zeilenLink(r);
            if (link) navigate(link);
        },
        [navigate],
    );

    /**
     * Frist/TWV direkt aus der Tabelle speichern (PO 03.08.2026). Optimistisch:
     * nach bestätigtem Backend-Erfolg wird NUR die betroffene Zeile lokal
     * gepatcht (kein Refetch, sonst springt die Scroll-Position). Bei Fehler
     * ein Toast + Resync über den Feed.
     */
    const [terminBusy, setTerminBusy] = useState<string | null>(null);
    const saveTermin = useCallback(
        async (row: UnifiedRow, feld: "twv" | "frist", wert: string | null) => {
            const busyKey = `${row.kind}-${row.id}-${feld}`;
            setTerminBusy(busyKey);
            // T-331: Eine neue TWV zieht eine leere oder frühere Frist mit.
            const aenderung = (feld === "twv" ? twvPatch(wert, row.frist) : { frist: wert }) as { twv?: string; frist?: string };
            try {
                if (row.kind === "vorgang") {
                    await vorgangService.updateVorgang(row.id, aenderung as any);
                    feed.patchByKey(`vorgang-${row.id}`, (it) =>
                        it.vorgang ? { ...it, vorgang: { ...it.vorgang, ...aenderung } } : it,
                    );
                } else {
                    await vorgangService.updateTodo(row.id, aenderung as any);
                    // Kind-To-do: in der Todo-Liste des Eltern-Vorgangs patchen,
                    // sonst in der eigenen Feed-Zeile.
                    if (row.vorgangId != null) {
                        feed.patchByKey(`vorgang-${row.vorgangId}`, (it) =>
                            it.vorgang
                                ? {
                                      ...it,
                                      vorgang: {
                                          ...it.vorgang,
                                          todos: (it.vorgang.todos ?? []).map((t) =>
                                              t.id === row.id ? { ...t, ...aenderung } : t,
                                          ),
                                      },
                                  }
                                : it,
                        );
                    }
                    feed.patchByKey(`todo-${row.id}`, (it) =>
                        it.todo ? { ...it, todo: { ...it.todo, ...aenderung } } : it,
                    );
                }
            } catch (err: any) {
                setToast({
                    message: String(err?.message ?? "").includes("403")
                        ? "Kein Recht, diesen Termin zu ändern."
                        : "Termin konnte nicht gespeichert werden.",
                    tone: "error",
                });
                reloadFeed();
            } finally {
                setTerminBusy(null);
            }
        },
        [feed.patchByKey, reloadFeed],
    );

    // People-Filter-Optionen (Beteiligte / Ersteller) aus dem User-Verzeichnis.
    const peopleOptions = useMemo<PeopleFilterOption[]>(() => personenFilterOptionen(users), [users]);

    // ─── Filter-Sheet-Helfer (Mobile, SP4) ───
    // Zahl aktiver Filter → Badge am Filter-Button + „Zurücksetzen"-Gate.
    const activeFilterCount = zaehlbareFilter({ filterValues, dateRangeValues, peopleValues, boFoValue });

    const toggleBeteiligt = useCallback((id: string) => {
        setPeopleValues((prev) => {
            const cur = prev["Beteiligte"] ?? [];
            const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
            const copy = { ...prev };
            if (next.length) copy["Beteiligte"] = next;
            else delete copy["Beteiligte"];
            return copy;
        });
    }, []);
    const toggleBoTeam = useCallback((id: string) => {
        setBoFoValue((v) => ({
            ...v,
            boTeamIds: v.boTeamIds.includes(id) ? v.boTeamIds.filter((x) => x !== id) : [...v.boTeamIds, id],
        }));
    }, []);
    const toggleFoUser = useCallback((id: string) => {
        setBoFoValue((v) => ({
            ...v,
            foUserIds: v.foUserIds.includes(id) ? v.foUserIds.filter((x) => x !== id) : [...v.foUserIds, id],
        }));
    }, []);
    const clearAllFilters = useCallback(() => {
        // Zurück auf die Werkseinstellung — und die ist seit T-156 „Offen".
        setFilterValues({ Status: STANDARD_STATUS_FILTER });
        setDateRangeValues({});
        setPeopleValues({});
        setBoFoValue({ boTeamIds: [], foUserIds: [] });
    }, []);

    /* ─── „Ohne Filter gibt es doch etwas" (PO 21.09.2026) ─────────────────
       Bleibt die Liste trotz Suche leer, liegt es meist an einem Filter. Statt
       den Anwender raten zu lassen, zählt die Liste einmal ohne Filter nach und
       bietet den Weg an. Hook und Hinweis teilt sich die Liste mit der
       Kontaktliste (`hooks/useOhneFilterProbe.ts`, `OhneFilterHinweis`). */
    const filterAnzahl = activeFilterCount;
    const { anzahl: ohneFilterTreffer, verwerfen: ohneFilterVerwerfen } = useOhneFilterProbe({
        treffer: feed.rows.length,
        laedt: feedLaedt,
        filterAnzahl,
        // `limit: 1` — gebraucht wird nur die Gesamtzahl aus X-Total-Count,
        // nicht die Zeilen. Die Sortierung bleibt dieselbe, damit die Zahl zu
        // dem passt, was nach dem Klick erscheint.
        zaehlen: (signal) =>
            vorgangService
                .getFeedPage(scope, 0, 1, signal, undefined, searchQuery, {}, sortRef.current)
                .then(({ total }) => total),
        schluessel: `${scope}|${searchQuery}|${sortKey}`,
    });

    /** Alle Filter fallen lassen. */
    const zeigeOhneFilter = useCallback(() => {
        clearAllFilters();
        ohneFilterVerwerfen();
    }, [clearAllFilters, ohneFilterVerwerfen]);

    /**
     * Was im leeren Zustand steht. Die Zahl ist Teil des Angebots: „Ohne
     * Filter anzeigen" allein ließe offen, ob sich der Klick lohnt.
     */
    const leerText: React.ReactNode = feedLaedt ? (
        "Lade Vorgänge …"
    ) : ohneFilterTreffer && ohneFilterTreffer > 0 ? (
        <OhneFilterHinweis
            anzahl={ohneFilterTreffer}
            onOhneFilter={zeigeOhneFilter}
            text="Keine Vorgänge mit den gesetzten Filtern."
        />
    ) : (
        "Keine Vorgänge."
    );

    // ─── Filter „Art & Betreff" (Betreff + Austausch, PO 01.11.2026) ───
    const betreffFilter = useMemo<BetreffAustauschWerte>(() => {
        const text = (v: string | boolean | undefined) => (typeof v === "string" ? v : "");
        const art = text(filterValues[COL_AUSTAUSCH]);
        return {
            titel: text(filterValues["Betreff"]),
            austausch: Object.prototype.hasOwnProperty.call(AUSTAUSCH_FILTER_LABEL, art) ? (art as FeedAustauschFilter) : undefined,
            mit: peopleValues[COL_AUSTAUSCH_MIT] ?? [],
            extern: text(filterValues[COL_AUSTAUSCH_EXTERN]),
        };
    }, [filterValues, peopleValues]);
    const betreffFilterUebernehmen = useCallback(
        (w: BetreffAustauschWerte) => {
            updateFilter("Betreff", w.titel || undefined);
            updateFilter(COL_AUSTAUSCH, w.austausch);
            updateFilter(COL_AUSTAUSCH_EXTERN, w.extern || undefined);
            updatePeople(COL_AUSTAUSCH_MIT, w.mit);
        },
        [updateFilter, updatePeople],
    );

    // ─── Spalten ───
    const columns: Column<UnifiedRow>[] = useMemo(() => {
        const cols: Column<UnifiedRow>[] = [
            /**
             * AUSWAHL für „Duplikate zusammenführen" (PO 21.09.2026).
             *
             * Nur an VORGANGSZEILEN. In den aufgeklappten To-do-Kindern steht
             * die Zelle leer: Ein To-do ist kein Duplikat eines Vorgangs, und
             * eine Checkbox dort ließe genau das vermuten.
             */
            {
                id: "select",
                className: "colcheck",
                pinned: true,
                frozen: true,
                width: 46,
                header: (
                    <Checkbox
                        checked={alleGewaehlt}
                        onChange={alleUmschalten}
                        ariaLabel="Alle Vorgänge auswählen"
                    />
                ),
                cell: (row) =>
                    row.kind === "vorgang" ? (
                        <Checkbox
                            checked={gewaehlteIds.has(row.id)}
                            onChange={() => auswahlUmschalten(row.id)}
                            ariaLabel="Vorgang auswählen"
                        />
                    ) : null,
            },
            {
                id: "Status",
                header: "Status",
                pinned: true,
                frozen: true,
                width: 186,
                minWidth: 166,
                sortable: true,
                filter: {
                    kind: "custom",
                    width: 220,
                    render: ({ value, onChange, close }) => {
                        const an = statusFilterWerte(value);
                        // Reihenfolge stabil (Refetch-Key); „nur" und „ohne
                        // importierte" schließen sich aus (T-244).
                        const umschalten = (opt: string) => onChange(statusFilterUmschalten(value, opt));
                        const option = (opt: string, label: string) => {
                            const aktiv = an.includes(opt);
                            return (
                                <button
                                    key={opt}
                                    type="button"
                                    role="menuitemcheckbox"
                                    aria-checked={aktiv}
                                    className={"rd-pop-opt" + (aktiv ? " on" : "")}
                                    onClick={() => umschalten(opt)}
                                >
                                    {label}
                                </button>
                            );
                        };
                        return (
                            <div className="rd-pop-list">
                                {STATUS_OPTIONS.map((opt) => option(opt, opt))}
                                {/* T-196: „Importiert" ist die Herkunft, kein Status —
                                    es schränkt die Auswahl darüber zusätzlich ein (UND):
                                    „Offen" + „nur importierte" = offene Importe.
                                    T-244: „ohne importierte" blendet die Altakten-
                                    Importe aus, ebenfalls kombinierbar. */}
                                <div
                                    role="separator"
                                    style={{ borderTop: "1px solid var(--line, #e5e1ea)", margin: "4px 0" }}
                                />
                                {option(IMPORTIERT, "nur importierte")}
                                {option(OHNE_IMPORT, "ohne importierte")}
                                {/* T-156: Ohne Auswahl hieße „Werkseinstellung Offen" —
                                    der Knopf setzt deshalb ausdrücklich „Alle". */}
                                {value !== ALLE_STATUS && (
                                    <button
                                        type="button"
                                        className="rd-pop-reset"
                                        onClick={() => {
                                            onChange(ALLE_STATUS);
                                            close();
                                        }}
                                    >
                                        Alle anzeigen
                                    </button>
                                )}
                            </div>
                        );
                    },
                },
                cell: (row) => {
                    // PO 06.10.2026 (T-196): Erledigen in der Liste ist wieder
                    // erlaubt — die Linie „rein lesend" vom 26.07.2026 ist
                    // aufgehoben. Ein kleiner Haken vor der Pille schaltet
                    // Offen/Erledigt um, nur wo der User den Vorgang bearbeiten
                    // darf (row.canDelete = canEditVorgang). Kind-To-dos werden
                    // weiter im Vorgang abgehakt.
                    if (row.kind === "todo") {
                        return <VorgangStatusPill status={row.status} size="small" />;
                    }
                    const istErledigt = row.status === "Erledigt";
                    const hakenTitel = istErledigt ? "Vorgang wieder öffnen" : "Vorgang als erledigt markieren";
                    return (
                        <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start", minWidth: 0 }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                {row.canDelete && (
                                    <button
                                        type="button"
                                        className={"vg-liste-haken" + (istErledigt ? " an" : "")}
                                        title={hakenTitel}
                                        aria-label={hakenTitel}
                                        aria-pressed={istErledigt}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            void statusUmschalten(row);
                                        }}
                                    >
                                        {I.check}
                                    </button>
                                )}
                                <VorgangStatusPill status={row.status} size="small" />
                            </span>
                            <VorgangProgressMini erledigt={row.erledigt} gesamt={row.gesamt} />
                            {row.importiert && (
                                <span className="vgd-persoenlich-badge" title="Aus einer alten Kundenakte übernommen">
                                    Importiert
                                </span>
                            )}
                        </div>
                    );
                },
            },
            {
                // Spalten-Id bleibt „Betreff" (Sortier-/Filter-Key, gespeicherte
                // Tabellen-Präferenzen). Angezeigt werden Art UND Betreff
                // nebeneinander (PO 02.08.2026): das Kürzel als Chip, dahinter
                // der Freitext — sofern es nicht selbst der Betreff ist
                // (Bestands-Vorgänge der Alt-Regel „die Art IST der Betreff").
                id: "Betreff",
                header: "Art & Betreff",
                pinned: true,
                frozen: true,
                width: 300,
                minWidth: 220,
                resizable: true,
                sortable: true,
                className: "kt-wrap",
                // Filter „Art & Betreff" (PO 01.11.2026): Betreff-Text und
                // Austausch (offen / mit mir / kein, mit wem) in EINEM Popover —
                // der Austausch steht ja auch hier, unter dem Betreff. Die
                // Werte liegen unter eigenen Schlüsseln (`Betreff`, `Austausch`,
                // `AustauschMit`, `AustauschExtern`); `betreffFilter` MUSS
                // deshalb in den Deps dieses Memos stehen (Stale-Closure).
                filter: {
                    kind: "custom",
                    width: 300,
                    active: istBetreffAustauschAktiv(betreffFilter),
                    render: ({ close }) => (
                        <BetreffAustauschFilter
                            werte={betreffFilter}
                            personen={peopleOptions}
                            onUebernehmen={betreffFilterUebernehmen}
                            close={close}
                        />
                    ),
                },
                cell: (row) => {
                    const key = `${row.kind}-${row.id}`;
                    const hasKids = !row.isChild && row.kind === "vorgang" && (row.children?.length ?? 0) > 0;
                    const isExp = expandedRef.current.has(key);
                    return (
                        <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
                            {hasKids ? (
                                <span
                                    role="button"
                                    aria-label={isExp ? "Zuklappen" : "Aufklappen"}
                                    title={isExp ? "Zuklappen" : "Aufklappen"}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleExpand(key);
                                    }}
                                    style={{
                                        cursor: "pointer",
                                        color: REFINED.aub,
                                        width: 22,
                                        height: 22,
                                        flexShrink: 0,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        borderRadius: 6,
                                        lineHeight: 1,
                                        marginTop: 1,
                                        backgroundColor: REFINED.aub50,
                                    }}
                                >
                                    {/* kvpIcons-Chevron statt Text-Glyph (▸/▾): zugeklappt zeigt
                                        es nach rechts (−90°), aufgeklappt nach unten (0°). */}
                                    <span
                                        style={{
                                            display: "inline-flex",
                                            transform: isExp ? "rotate(0deg)" : "rotate(-90deg)",
                                            transition: "transform 0.16s ease",
                                        }}
                                    >
                                        {I.chevD}
                                    </span>
                                </span>
                            ) : (
                                <span style={{ width: row.isChild ? 24 : 22, flexShrink: 0, display: "inline-block" }} />
                            )}
                            <span
                                style={{
                                    whiteSpace: "normal",
                                    overflowWrap: "anywhere",
                                    wordBreak: "break-word",
                                    fontWeight: row.isChild ? 400 : 600,
                                    color: row.isChild ? REFINED.ink2 : REFINED.ink,
                                }}
                            >
                                {zeigtArtChip(row.vorgangsart) && (
                                    <span
                                        style={{
                                            display: "inline-block",
                                            marginRight: 6,
                                            padding: "1px 7px",
                                            borderRadius: 999,
                                            backgroundColor: REFINED.aub50,
                                            color: REFINED.aub,
                                            fontSize: "0.72rem",
                                            fontWeight: 800,
                                            letterSpacing: "0.04em",
                                            verticalAlign: "1px",
                                        }}
                                    >
                                        {(row.vorgangsart as string).trim()}
                                    </span>
                                )}
                                {/* Ohne eigenen Betreff steht die Art bereits im Chip —
                                    dann bleibt hier nur der dezente Platzhalter. */}
                                {betreffOhneArt(row.vorgangsart, row.titel) || (
                                    <span style={{ color: REFINED.ink2, fontWeight: 400 }}>
                                        {zeigtArtChip(row.vorgangsart) ? "" : "Ohne Betreff"}
                                    </span>
                                )}
                                {/* T-302 / PO 01.11.2026: Wo eine Rücksprache ansteht,
                                    steht es unter dem Betreff — „Austausch · DC, CLL",
                                    im Hover alle Namen, Externe als „(extern)". Die
                                    Spalte „Austausch" gibt es zusätzlich (ausgeblendet). */}
                                {row.kind === "vorgang" && row.austausch && (
                                    <span style={{ display: "block", marginTop: 4 }}>
                                        <AustauschChip austausch={row.austausch} />
                                    </span>
                                )}
                            </span>
                        </div>
                    );
                },
            },
            {
                // T-302 / PO 01.11.2026: „in der Vorgangsübersicht filterbar nach
                // Austausch; dort nicht nur A, sondern ‚Austausch' anzeigen, und
                // beim Hover sieht man genau, mit wem." Werksseitig AUSGEBLENDET
                // wie „Ersteller" — das Kennzeichen steht unter dem Betreff, und
                // gefiltert wird im Filter „Art & Betreff" (keine zweite
                // Bedienung hier).
                id: COL_AUSTAUSCH,
                header: "Austausch",
                hideable: true,
                collapsible: true,
                minWidth: 120,
                width: 150,
                resizable: true,
                cell: (row) =>
                    row.kind === "vorgang" && row.austausch ? <AustauschChip austausch={row.austausch} /> : null,
            },
            {
                id: "Kontakt",
                header: "Kontakt",
                className: "kt-wrap",
                hideable: true,
                collapsible: true,
                minWidth: 150,
                resizable: true,
                sortable: true,
                filter: { kind: "text" },
                cell: (row) => {
                    // I1/E07: kein Kontakt, aber auch keine leere Zelle — das
                    // Badge erklärt, warum dort nichts steht.
                    if (row.persoenlich) {
                        return <span className="vgd-persoenlich-badge">Persönlich</span>;
                    }
                    if (!row.kundeLabel || row.kundeLabel === "—") return null;
                    const kontaktId = row.kunde?.id ?? null;
                    if (kontaktId == null) {
                        return (
                            <span style={{ whiteSpace: "normal", overflowWrap: "anywhere", wordBreak: "break-word" }}>
                                {row.kundeLabel}
                            </span>
                        );
                    }
                    // Klick öffnet die Kontakt-Detailseite — stopPropagation, weil
                    // der Zeilen-Klick sonst den Vorgang öffnet. Bewusst OHNE
                    // state.k-Snapshot navigieren (der würde in ContactDetails
                    // frische Daten überdecken, vgl. Kontaktstatus-Fix).
                    return (
                        <button
                            type="button"
                            title="Kontakt öffnen"
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/kontakte/edit/${kontaktId}`);
                            }}
                            style={{
                                background: "none",
                                border: "none",
                                padding: 0,
                                font: "inherit",
                                color: "inherit",
                                cursor: "pointer",
                                textAlign: "left",
                                textDecoration: "underline",
                                textDecorationColor: "color-mix(in srgb, currentColor 35%, transparent)",
                                textUnderlineOffset: 2,
                                whiteSpace: "normal",
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                            }}
                        >
                            {row.kundeLabel}
                        </button>
                    );
                },
            },
            {
                id: "Verantwortung",
                header: (
                    <span className="kt-vr-head">
                        <span className="kt-vr-title">K-Verantwortlich</span>
                        <span className="kt-vr-sub">BO | FO</span>
                    </span>
                ),
                hideable: true,
                collapsible: true,
                minWidth: 130,
                filter: { kind: "boFo", boOptions: teamOptions, foOptions: userOptions },
                cell: (row) => (row.kunde ? <FoBoBadge boTeam={row.kunde.boTeam} foUser={row.kunde.foUser} /> : null),
            },
            {
                id: "Beteiligte",
                header: "Beteiligte",
                hideable: true,
                collapsible: true,
                minWidth: 120,
                // T-245: Person per Name oder Kürzel, optional eingeschränkt auf
                // eine Rolle (HV / Bearbeiter / zu informieren).
                filter: { kind: "people", options: peopleOptions, roles: BETEILIGT_ROLLEN_OPTIONEN },
                cell: (row) => <PeopleCell groups={row.beteiligte} />,
            },
            // TWV steht bewusst VOR der Frist: die Wiedervorlage sagt, wann man
            // sich das Thema wieder vornimmt — sie wird häufiger gelesen als die
            // Deadline dahinter.
            {
                id: "TWV",
                header: "TWV",
                hideable: true,
                collapsible: true,
                minWidth: 120,
                sortable: true,
                filter: { kind: "dateRange" },
                cell: (row) => (
                    <DateEditCell
                        iso={row.twv}
                        variant="twv"
                        editable={row.canDelete}
                        speichert={terminBusy === `${row.kind}-${row.id}-twv`}
                        onSave={(wert) => void saveTermin(row, "twv", wert)}
                    />
                ),
            },
            {
                id: "Frist",
                header: "Frist",
                hideable: true,
                collapsible: true,
                minWidth: 120,
                sortable: true,
                filter: { kind: "dateRange" },
                cell: (row) => (
                    <DateEditCell
                        iso={row.frist}
                        overdue={isOverdueFrist(row)}
                        editable={row.canDelete}
                        speichert={terminBusy === `${row.kind}-${row.id}-frist`}
                        onSave={(wert) => void saveTermin(row, "frist", wert)}
                    />
                ),
            },
            {
                id: "Ersteller",
                header: "Ersteller",
                hideable: true,
                collapsible: true,
                minWidth: 130,
                filter: { kind: "people", options: peopleOptions },
                cell: (row) =>
                    row.ersteller ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 7, minWidth: 0 }}>
                            <InitialsAvatar name={row.ersteller.name} size={24} userId={row.ersteller.userId} label={row.ersteller.initials || undefined} />
                            <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13 }}>
                                {row.ersteller.name}
                            </span>
                        </span>
                    ) : null,
            },
            // Erstellungsdatum: fachlich selten gebraucht, aber die
            // Standard-Sortierung („neueste zuerst") hängt daran — deshalb als
            // Spalte vorhanden und werksseitig eingeklappt (ein Klick auf den
            // schmalen Streifen blendet sie auf).
            {
                id: "Erstellt",
                header: "Erstellt",
                hideable: true,
                collapsible: true,
                minWidth: 110,
                sortable: true,
                cell: (row) => <DateCell iso={row.createdAt} variant="erstellt" />,
            },
        ];
        // Keine Aktionsspalte mehr (PO 06.10.2026, T-196): Gelöscht wird wie bei
        // den Kontakten über die Fußleiste, sobald etwas ausgewählt ist.
        return cols;
        // `gewaehlteIds`/`alleGewaehlt`/die beiden Umschalter MÜSSEN hier stehen
        // (PO 21.09.2026: „bleibt weiß"). Die Checkbox-Zelle liest den Zustand
        // aus der Closure dieses Memos; fehlte er in den Deps, blieben die
        // Spalten beim Anhaken unverändert und die Zelle sähe für immer das
        // leere Set vom ersten Render — der Haken erschien nie, obwohl der
        // State lief (die Fußleiste zählte korrekt mit, sie liest ihn direkt).
        // Dasselbe Dep-Muster wie ContactTableRedesign, wo die Auswahl seit je
        // funktioniert. Das Aufklappen kommt weiter über `expandedRef` — das
        // ist ein Ref genau deshalb, weil es NICHT in den Deps steht.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin, currentUserId, peopleOptions, teamOptions, userOptions, statusUmschalten, toggleExpand, navigate, saveTermin, terminBusy,
        gewaehlteIds, alleGewaehlt, auswahlUmschalten, alleUmschalten, betreffFilter, betreffFilterUebernehmen]);

    const entryCount = feed.total;

    const tableSubBar = (
        <div className="kt-subbar">
            {/* Links stand bis zum 21.09.2026 „Mails zu meinen Vorgängen" und
                führte auf `/vorgaenge/mails`. Knopf und Seite sind entfallen
                (PO) — den Schriftverkehr zeigt der Verlauf im Vorgang selbst.
                Die Zählung trägt weiter `marginRight: auto` und schiebt die
                drei Knöpfe, die auf die Tabelle wirken, nach rechts. */}
            <span style={{ marginRight: "auto", fontSize: 12.5, fontWeight: 600, color: "var(--ink-3)" }}>
                {entryCount != null ? `${entryCount} ${entryCount === 1 ? "Eintrag" : "Einträge"}` : ""}
            </span>
            <button
                type="button"
                className="tbar-btn"
                title="Gelöschte Vorgänge ansehen und wiederherstellen"
                onClick={() => setPapierkorbOpen(true)}
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
                {I.recycle}Papierkorb
            </button>
            <ColumnsMenu
                columns={columns}
                columnVisibility={columnVisibility}
                onColumnVisibilityChange={setColumnVisibility}
                collapsedColumns={collapsedColumns}
                onToggleCollapsed={toggleCollapsed}
            />
        </div>
    );

    /**
     * Speichern-Leiste am unteren Rand.
     *
     * Sie öffnet sich bei einer geänderten SPALTENANSICHT (Reihenfolge,
     * Sichtbarkeit, Breite, Eingeklappt) und auf ausdrücklichen Wunsch über das
     * Rechtsklick-Menü. Filter und Bereich lösen sie NICHT aus — die ändert man
     * im Tagesgeschäft ständig, und eine Leiste, die dabei jedes Mal nach einer
     * globalen Einstellung fragt, wäre nur Lärm. Gespeichert werden sie
     * trotzdem mit, sobald jemand hier speichert.
     */
    /**
     * Zusatz-Einträge im Spalten-Kontextmenü. „Tabellen-Präferenzen" ist der
     * ausdrückliche Weg zur Speichern-Leiste — ohne ihn käme man an das
     * Speichern von Bereich und Filtern gar nicht heran, solange die
     * Spaltenansicht unverändert ist.
     */
    const prefsMenuItems = useMemo(
        () => [
            {
                id: "prefs",
                label: "Tabellen-Präferenzen …",
                icon: "⚙",
                onSelect: () => {
                    setDismissedViewKey(null);
                    setPrefsRequested(true);
                },
            },
        ],
        [],
    );

    /** Rechtsklick-Menü der Bereichsauswahl (Persönlich/Team/Alle). */
    const scopeContextMenu = (
        <RdContextMenu
            position={scopeMenu}
            onClose={() => setScopeMenu(null)}
            title="Sichtbarkeitsbereich"
            items={[
                {
                    id: "scope-save",
                    label: viewSaving ? "Speichert …" : "Als Standard speichern",
                    icon: "✓",
                    disabled: viewSaving,
                    onSelect: () => void savePrefs(),
                },
                { kind: "sep" },
                {
                    id: "scope-prefs",
                    label: "Tabellen-Präferenzen …",
                    icon: "⚙",
                    onSelect: () => {
                        setDismissedViewKey(null);
                        setPrefsRequested(true);
                    },
                },
            ]}
        />
    );

    const viewSaveBar = prefsBarOpen ? (
        <div className="cd-savebar" role="status">
            <div className="cd-savebar-left">
                <button className="cd-save" disabled={viewSaving} onClick={() => void savePrefs()}>
                    {viewSaving ? "Speichert …" : "Ansicht, Bereich und Filter als Standard speichern"}
                </button>
                <button className="tbar-btn" disabled={viewSaving} onClick={dismissPrefsBar}>
                    Verwerfen
                </button>
            </div>
            <span className="cd-savebar-note">
                {viewDirty
                    ? "Spaltenansicht geändert — ohne Speichern gilt beim nächsten Laden wieder der Standard. "
                    : "Gespeichert werden Spaltenansicht, Sichtbarkeitsbereich und Filter. "}
                <button type="button" className="vg-linkbtn" onClick={resetViewToFactory}>
                    Werkseinstellung
                </button>
            </span>
        </div>
    ) : null;

    /**
     * Rechtsklick auf die Bereichsauswahl — dasselbe Angebot wie am Spaltenkopf,
     * nur hier. Der gewählte Bereich ist eine Einstellung wie die Spaltenansicht
     * auch, und man erwartet ihn dort zu speichern, wo man ihn umschaltet.
     */
    const scopeBar = (
        <div
            className="rbac-seg"
            role="group"
            aria-label="Sichtbarkeitsbereich"
            onContextMenu={(e) => {
                e.preventDefault();
                setScopeMenu({ x: e.clientX, y: e.clientY });
            }}
        >
            {scopeOptions.map((o) => (
                <button
                    key={o.key}
                    type="button"
                    className={"rbac-seg-btn" + (scope === o.key ? " on" : "")}
                    aria-pressed={scope === o.key}
                    onClick={() => onScopeChange(o.key)}
                >
                    <span className="rbac-dot" />
                    {o.label}
                </button>
            ))}
        </div>
    );

    // ─── Mobile (Phone): Kopf-Leiste Zeile 2 (Scope + Filter) + Karten + Sheet (SP4) ───
    const mobileFilterBar = (
        <div className="ct-mobilebar">
            {scopeBar}
            <button
                type="button"
                className={"tbtn" + (activeFilterCount > 0 ? " primary" : "")}
                onClick={() => setFilterSheetOpen(true)}
            >
                {FunnelIcon}Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
            </button>
        </div>
    );

    /** Einheitlicher Status-Chip fürs Karten-Deck (F1/B3):
     *  Kind-To-do → read-only aubergine Status-Pille; Vorgang → read-only Pille
     *  aus dem abgeleiteten Status + kleiner „n/m"-Zusatz (nur mit To-dos). */
    const cardStatus = (item: UnifiedRow) => {
        if (item.kind === "todo") return <VorgangStatusPill status={item.status} size="small" />;
        return (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <VorgangStatusPill status={item.status} size="small" />
                {item.gesamt > 0 && (
                    <span style={{ fontSize: 11.5, fontWeight: 600, color: REFINED.ink3 }}>
                        {item.erledigt}/{item.gesamt}
                    </span>
                )}
            </span>
        );
    };

    const cardList = (
        <div className="ct-cards" id="vorgaenge-cards-scroll" ref={scrollRef}>
            {rows.length === 0 ? (
                <div className="empty">{leerText}</div>
            ) : (
                rows.map((item) => {
                    const overdue = isOverdueFrist(item);
                    const rel = formatRelativeDay(item.frist);
                    const hasKontakt = !!item.kundeLabel && item.kundeLabel !== "—";
                    const hasVerantw = !!(item.kunde && (item.kunde.boTeam || item.kunde.foUser));
                    const hasFrist = !!item.frist;
                    const hasTwv = !!item.twv;
                    const hasBeteiligte = unionPeople(item.beteiligte).length > 0;
                    return (
                        <div key={`${item.kind}-${item.id}`} className="ct-card" onClick={() => openRow(item)}>
                            {/* Kopf: einheitlicher Status-Chip + relative Frist (rot bei überfällig). */}
                            <div className="ct-card-head">
                                {cardStatus(item)}
                                <span style={{ flex: 1 }} />
                                {(overdue || rel) && (
                                    <span className="vg-card-rel" style={{ color: overdue ? REFINED.err : REFINED.ink2 }}>
                                        {overdue ? "überfällig" : rel}
                                    </span>
                                )}
                            </div>
                            {/* Titel — voll umbrechend, fett. */}
                            <div className="vg-card-title">
                                <span className="ct-card-name">{item.titel}</span>
                                {item.austausch && (
                                    <span style={{ display: "block", marginTop: 4 }}>
                                        <AustauschChip austausch={item.austausch} />
                                    </span>
                                )}
                            </div>
                            {/* Zeilen — nur wenn befüllt (kein „—"-Rauschen). */}
                            {(hasKontakt || hasVerantw) && (
                                <div className="ct-card-body">
                                    {hasKontakt && (
                                        <div className="ct-row">
                                            <span className="ct-k">Kontakt</span>
                                            <span className="ct-v">{item.kundeLabel}</span>
                                        </div>
                                    )}
                                    {hasVerantw && (
                                        <div className="ct-row">
                                            <span className="ct-k">K-Verantwortlich</span>
                                            <span
                                                className="ct-v"
                                                style={{ overflow: "visible", display: "inline-flex", justifyContent: "flex-end", minWidth: 0 }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <FoBoBadge boTeam={item.kunde!.boTeam} foUser={item.kunde!.foUser} />
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}
                            {/* Fuß: Beteiligte-Avatare links, Frist/TWV-Doppeldatum rechts. */}
                            {(hasBeteiligte || hasFrist || hasTwv) && (
                                <div className="ct-card-foot">
                                    <span className="vg-card-foot-people" onClick={(e) => e.stopPropagation()}>
                                        {hasBeteiligte ? <PeopleCell groups={item.beteiligte} /> : null}
                                    </span>
                                    <span className="vg-card-foot-dates">
                                        {hasFrist && (
                                            <span
                                                style={{ color: overdue ? REFINED.err : undefined }}
                                                title={overdue ? "Frist (überfällig)" : "Frist"}
                                            >
                                                <Flag16Regular /> {deIsoDate(item.frist)}
                                            </span>
                                        )}
                                        {hasTwv && (
                                            <span title="Wiedervorlage (TWV)">
                                                <Clock16Regular /> {deIsoDate(item.twv)}
                                            </span>
                                        )}
                                    </span>
                                </div>
                            )}
                        </div>
                    );
                })
            )}
            {feed.hasMore ? (
                <button className="ct-more" onClick={() => feed.loadMore()} disabled={feed.loading}>
                    {feed.loading ? "Lädt …" : "Mehr laden"}
                </button>
            ) : null}
        </div>
    );

    const filterSheet = filterSheetOpen ? (
        <div className="ct-sheet-scrim" onClick={() => setFilterSheetOpen(false)}>
            <div className="ct-sheet" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                <div className="ct-sheet-head">
                    <h3>Filter</h3>
                    <button className="dm-x" aria-label="Schließen" onClick={() => setFilterSheetOpen(false)}>
                        {CloseIcon}
                    </button>
                </div>
                <div className="ct-sheet-body">
                    <div className="ct-sheet-fld">
                        <span className="ct-sheet-lbl">Status</span>
                        <select
                            className="sel"
                            value={(filterValues["Status"] as string) ?? ""}
                            onChange={(e) => updateFilter("Status", e.target.value || undefined)}
                        >
                            <option value={ALLE_STATUS}>Alle</option>
                            {STATUS_OPTIONS.map((s) => (
                                <option key={s} value={s}>
                                    {s}
                                </option>
                            ))}
                            <option value={IMPORTIERT}>Nur importierte</option>
                            {/* T-196: UND-verknüpft — die offenen Importe abarbeiten. */}
                            <option value="Offen,Importiert">Offen und importiert</option>
                            <option value="Erledigt,Importiert">Erledigt und importiert</option>
                            {/* T-244: Importe ausblenden, auch zusammen mit dem Status. */}
                            <option value={OHNE_IMPORT}>Ohne importierte</option>
                            <option value="Offen,OhneImport">Offen ohne importierte</option>
                            <option value="Erledigt,OhneImport">Erledigt ohne importierte</option>
                        </select>
                    </div>
                    <SheetDateFilter
                        label="TWV"
                        value={dateRangeValues["TWV"]}
                        onChange={(next) => updateDateRange("TWV", next)}
                    />
                    <SheetDateFilter
                        label="Frist"
                        value={dateRangeValues["Frist"]}
                        onChange={(next) => updateDateRange("Frist", next)}
                    />
                    {peopleOptions.length > 0 ? (
                        <div className="ct-sheet-fld">
                            <span className="ct-sheet-lbl">Beteiligte Personen</span>
                            <div className="ct-sheet-checks">
                                {peopleOptions.map((o) => (
                                    <label key={o.id} className="ct-sheet-check">
                                        <input
                                            type="checkbox"
                                            checked={(peopleValues["Beteiligte"] ?? []).includes(o.id)}
                                            onChange={() => toggleBeteiligt(o.id)}
                                        />
                                        {o.label}
                                        {o.initials ? ` (${o.initials})` : ""}
                                    </label>
                                ))}
                            </div>
                            {/* T-245: Rolle der gewählten Person — keine = alle. */}
                            <span className="ct-sheet-lbl">… in der Rolle</span>
                            <div className="ct-sheet-checks">
                                {BETEILIGT_ROLLEN_OPTIONEN.map((r) => (
                                    <label key={r.id} className="ct-sheet-check">
                                        <input
                                            type="checkbox"
                                            checked={(peopleValues["Beteiligte"] ?? []).includes(PERSONEN_ROLLE_PRAEFIX + r.id)}
                                            onChange={() => toggleBeteiligt(PERSONEN_ROLLE_PRAEFIX + r.id)}
                                        />
                                        {r.label}
                                    </label>
                                ))}
                            </div>
                        </div>
                    ) : null}
                    {teamOptions.length > 0 ? (
                        <div className="ct-sheet-fld">
                            <span className="ct-sheet-lbl">K-Verantwortlich · Team</span>
                            <div className="ct-sheet-checks">
                                {teamOptions.map((o) => (
                                    <label key={o.id} className="ct-sheet-check">
                                        <input type="checkbox" checked={boFoValue.boTeamIds.includes(o.id)} onChange={() => toggleBoTeam(o.id)} />
                                        {o.label}
                                    </label>
                                ))}
                            </div>
                        </div>
                    ) : null}
                    {userOptions.length > 0 ? (
                        <div className="ct-sheet-fld">
                            <span className="ct-sheet-lbl">K-Verantwortlich · Person</span>
                            <div className="ct-sheet-checks">
                                {userOptions.map((o) => (
                                    <label key={o.id} className="ct-sheet-check">
                                        <input type="checkbox" checked={boFoValue.foUserIds.includes(o.id)} onChange={() => toggleFoUser(o.id)} />
                                        {o.label}
                                    </label>
                                ))}
                            </div>
                        </div>
                    ) : null}
                </div>
                <div className="ct-sheet-foot">
                    <button className="tbar-btn" disabled={activeFilterCount === 0} onClick={clearAllFilters}>
                        Zurücksetzen
                    </button>
                    <button className="cd-save" onClick={() => setFilterSheetOpen(false)}>
                        Fertig
                    </button>
                </div>
            </div>
        </div>
    ) : null;

    return (
        <div className="kvp-rd kvp-rd-table-page">
            {/* Kopfleiste (A17): Suche · „+ Vorgang" · Sichtbarkeitsbereich (Desktop).
                Mobil (A16): nur Suche + „+ Vorgang" — der Scope wandert in die
                zweite Zeile (ct-mobilebar), damit die Leiste nicht überläuft. */}
            <div className="bar">
                <div className="search">
                    {I.search}
                    <input placeholder="Nach Vorgängen suchen …" value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <button type="button" className="tbtn primary" onClick={() => openVorgangCreateFlow(openWindow)}>
                    {I.plus}Vorgang
                </button>
                <div className="spacer" style={{ flex: 1 }} />
                {!isPhone && scopeBar}
            </div>

            {effectiveView === "tabelle" ? (
                <>
                <DataTable
                    columns={columns}
                    rows={rows}
                    rowKey={(r) => `${r.kind}-${r.id}`}
                    onRowClick={openRow}
                    /* ZWEI ZÜGE, EIN ZIEL (PO 21. und 22.09.2026).
                       Vorgang auf Vorgang → verschmelzen; To-do auf einen
                       fremden Vorgang → das To-do wechselt die Heimat. Seit dem
                       28.09.2026 dazu: To-do auf ein GESCHWISTER-To-do → der
                       Zusammenführen-Dialog (`todoZug.ts`, `darfAufTodo`). */
                    rowDraggable={(r) => r.kind === "vorgang" || r.kind === "todo"}
                    onRowDragStart={(r, _i, e) => {
                        if (r.kind === "vorgang") {
                            const zug: VorgangZug = {
                                id: r.id,
                                titel: r.titel ?? "",
                                hatTodos: hatTodos(r),
                            };
                            e.dataTransfer.setData(ZIEHTYP_VORGANG, JSON.stringify(zug));
                            gezogenRef.current = { art: "vorgang", id: r.id };
                        } else {
                            const zug: TodoZug = {
                                id: r.id,
                                titel: r.titel ?? "",
                                vorgangId: r.vorgangId ?? null,
                            };
                            e.dataTransfer.setData(ZIEHTYP_TODO, JSON.stringify(zug));
                            // Für das Aufleuchten zählt der HEIMAT-Vorgang, nicht
                            // die To-do-Id: Verboten ist der Zug auf den Vorgang,
                            // in dem es schon liegt.
                            gezogenRef.current = { art: "todo", id: r.vorgangId ?? -1, todoId: r.id };
                        }
                        e.dataTransfer.effectAllowed = "move";
                    }}
                    rowDropTarget={(r, _i, e) => {
                        const zug = gezogenRef.current;
                        // Ein To-do nimmt nur ein Geschwister an (PO 28.09.2026).
                        if (r.kind === "todo") {
                            return (
                                istTodoZug(e.dataTransfer.types) &&
                                zug?.art === "todo" &&
                                zug.todoId != null &&
                                darfAufTodo({ id: zug.todoId, vorgangId: zug.id }, { id: r.id, vorgangId: r.vorgangId })
                            );
                        }
                        if (r.kind !== "vorgang") return false;
                        if (istVorgangZug(e.dataTransfer.types)) {
                            return zug?.art === "vorgang" && darfHierher(zug.id, r.id);
                        }
                        if (istTodoZug(e.dataTransfer.types)) {
                            return zug?.art === "todo" && darfTodoHierher(zug.id, r.id);
                        }
                        return false;
                    }}
                    onRowDrop={(r, _i, e) => {
                        if (r.kind === "todo") {
                            const roh = e.dataTransfer.getData(ZIEHTYP_TODO);
                            gezogenRef.current = null;
                            try {
                                const zug = JSON.parse(roh) as TodoZug;
                                if (r.vorgangId != null && darfAufTodo(zug, { id: r.id, vorgangId: r.vorgangId })) {
                                    setTodoMerge({ vorgangId: r.vorgangId, quelleId: zug.id, zielId: r.id });
                                }
                            } catch {
                                // Fremder Zug mit unserem Typ — nichts tun.
                            }
                            return;
                        }
                        if (r.kind !== "vorgang") return;
                        // Erst JETZT gibt der Browser die Nutzlast heraus.
                        const rohVorgang = e.dataTransfer.getData(ZIEHTYP_VORGANG);
                        const rohTodo = e.dataTransfer.getData(ZIEHTYP_TODO);
                        gezogenRef.current = null;
                        try {
                            if (rohVorgang) {
                                const zug = JSON.parse(rohVorgang) as VorgangZug;
                                if (!darfHierher(zug.id, r.id)) return;
                                setPendingMerge({
                                    quelle: zug,
                                    ziel: { id: r.id, titel: r.titel ?? "", hatTodos: hatTodos(r) },
                                });
                            } else if (rohTodo) {
                                const zug = JSON.parse(rohTodo) as TodoZug;
                                if (!darfTodoHierher(zug.vorgangId, r.id)) return;
                                setPendingTodoZug({
                                    todo: zug,
                                    quelleTitel: titelVonVorgang(zug.vorgangId),
                                    ziel: { id: r.id, titel: r.titel ?? "" },
                                });
                            }
                        } catch {
                            // Fremder Zug mit unserem Typ — nichts tun ist die
                            // richtige Antwort, ein Fehlerton wäre Lärm.
                        }
                    }}
                    emptyText={leerText}
                    tableClassName="kt-dense"
                    rowChildren={(r) => (r.kind === "vorgang" ? r.children ?? [] : [])}
                    expandedKeys={expanded}
                    sort={sort}
                    onSortChange={setSort}
                    filterValues={filterValues}
                    onFilterChange={updateFilter}
                    boFoValue={boFoValue}
                    onBoFoChange={setBoFoValue}
                    dateRangeValues={dateRangeValues}
                    onDateRangeChange={updateDateRange}
                    peopleValues={peopleValues}
                    onPeopleChange={updatePeople}
                    columnOrder={columnOrder}
                    onColumnOrderChange={setColumnOrder}
                    columnVisibility={columnVisibility}
                    onColumnVisibilityChange={setColumnVisibility}
                    columnWidths={columnWidths}
                    onColumnWidthsChange={setColumnWidths}
                    collapsedColumns={collapsedColumns}
                    onToggleCollapsed={toggleCollapsed}
                    extraMenuItems={prefsMenuItems}
                    onLoadMore={feed.loadMore}
                    hasMore={feed.hasMore}
                    loading={feedLaedt}
                    loadingText="Lade Vorgänge …"
                    scrollContainerId="vorgaenge-table-scroll"
                    scrollRef={scrollRef}
                    aboveTable={tableSubBar}
                    rowSelected={(r) => r.kind === "vorgang" && gewaehlteIds.has(r.id)}
                />
                {/* Erscheint erst mit einer Auswahl (PO 21.09.2026). Ab ZWEI
                    Vorgängen ergibt Zusammenführen einen Sinn — bei einem gäbe
                    es nichts hineinzuführen, deshalb steht der Knopf dann nur
                    erklärend da. */}
                <TableBottomBar
                    selectionCount={gewaehlteIds.size}
                    filteredTotal={entryCount}
                    filterActive={false}
                    actions={
                        <>
                            <button
                                type="button"
                                className="tbar-btn"
                                disabled={gewaehlteIds.size < 2}
                                title={
                                    gewaehlteIds.size < 2
                                        ? "Mindestens zwei Vorgänge auswählen"
                                        : "Die ausgewählten Vorgänge zu einem zusammenführen"
                                }
                                onClick={() => setDupOffen(true)}
                            >
                                {I.merge}Zusammenführen
                            </button>
                            {/* T-196 (PO 06.10.2026): Erledigen, wieder öffnen und
                                löschen für alle angehakten Vorgänge — Löschen wie bei
                                den Kontakten über die Leiste, nicht mehr je Zeile. */}
                            <button
                                type="button"
                                className="tbar-btn"
                                disabled={zuVieleGewaehlt}
                                title={zuVieleGewaehlt ? zuVieleText : "Die ausgewählten Vorgänge als erledigt markieren"}
                                onClick={() => setSammelFrage("Erledigt")}
                            >
                                {I.check}Erledigt setzen
                            </button>
                            <button
                                type="button"
                                className="tbar-btn"
                                disabled={zuVieleGewaehlt}
                                title={zuVieleGewaehlt ? zuVieleText : "Die ausgewählten Vorgänge wieder öffnen"}
                                onClick={() => setSammelFrage("Offen")}
                            >
                                Wieder öffnen
                            </button>
                            <button
                                type="button"
                                className="tbar-del"
                                disabled={zuVieleGewaehlt || !darfAuswahlLoeschen}
                                title={
                                    zuVieleGewaehlt
                                        ? zuVieleText
                                        : darfAuswahlLoeschen
                                          ? "Die ausgewählten Vorgänge in den Papierkorb legen"
                                          : "Keinen der ausgewählten Vorgänge dürfen Sie löschen"
                                }
                                onClick={() => setSammelFrage("loeschen")}
                            >
                                {I.trash}Löschen
                            </button>
                            <button
                                type="button"
                                className="tbar-btn"
                                onClick={() => setGewaehlteIds(new Set())}
                            >
                                Auswahl aufheben
                            </button>
                        </>
                    }
                />
                {viewSaveBar}
                </>
            ) : (
                /* Mobile (Phone, SP4): Kopf-Zeile 2 (Scope + Filter), Karten, Sheet. */
                <>
                    {mobileFilterBar}
                    {cardList}
                    {filterSheet}
                </>
            )}

            {/* Außerhalb beider Zweige: die Bereichsauswahl steht auf dem Phone in
                der mobilen Leiste, und ein Long-Press löst dort ebenfalls
                `contextmenu` aus. Stünde das Menü nur im Tabellen-Zweig, ginge der
                Rechtsklick dort ins Leere. */}
            {scopeContextMenu}

            {/* Papierkorb: gelöschte Vorgänge (Soft-Delete, samt To-dos) zurückholen. */}
            <PapierkorbDialog
                open={papierkorbOpen}
                onClose={() => setPapierkorbOpen(false)}
                onRestored={reloadFeed}
            />

            {/* Sammelaktionen der Fußleiste (T-196) — Rückfrage mit Anzahl im
                eigenen kvp-rd-Dialog. Erledigen/Öffnen neutral, Löschen rot. */}
            <ConfirmDialog
                open={sammelFrage !== null}
                tone={sammelFrage === "loeschen" ? "danger" : "primary"}
                title={
                    sammelFrage === "loeschen"
                        ? `${anzahlVorgaenge(gewaehlteIds.size)} löschen?`
                        : sammelFrage === "Erledigt"
                          ? `${anzahlVorgaenge(gewaehlteIds.size)} als erledigt markieren?`
                          : `${anzahlVorgaenge(gewaehlteIds.size)} wieder öffnen?`
                }
                message={
                    sammelFrage === "loeschen" ? (
                        <>
                            Die ausgewählten Vorgänge wandern samt ihren To-dos in den Papierkorb und lassen
                            sich von dort einzeln wiederherstellen. Vorgänge, die Sie nicht löschen dürfen,
                            werden übersprungen.
                        </>
                    ) : sammelFrage === "Erledigt" ? (
                        <>
                            Die offenen To-dos dieser Vorgänge werden mit erledigt. TWV und Frist werden dabei
                            geleert. Vorgänge, die Sie nicht bearbeiten dürfen, werden übersprungen.
                        </>
                    ) : (
                        <>
                            Die Vorgänge stehen danach wieder auf „Offen“. Waren alle To-dos eines Vorgangs
                            erledigt, werden sie mit geöffnet. Vorgänge, die Sie nicht bearbeiten dürfen,
                            werden übersprungen.
                        </>
                    )
                }
                confirmLabel={
                    sammelFrage === "loeschen" ? "Löschen" : sammelFrage === "Erledigt" ? "Erledigt setzen" : "Wieder öffnen"
                }
                busy={sammelBusy}
                onConfirm={() => void sammelAusfuehren()}
                onCancel={() => setSammelFrage(null)}
            />
            {/* „Vorgang auf Vorgang" (PO 21.09.2026). Derselbe eigene Dialog
                wie beim Löschen — ausdrücklich kein Fluent-Popup. Ton NICHT
                `danger`: Es geht nichts verloren, der Quellvorgang ist aus dem
                Papierkorb zurückzuholen. Rot wäre hier eine Drohung. */}
            <ConfirmDialog
                open={!!pendingMerge}
                title={
                    pendingMerge
                        ? (zugWeg(pendingMerge.quelle, pendingMerge.ziel) === "alsTodo"
                              ? alsTodoFrage
                              : verschmelzFrage)(pendingMerge.quelle.titel, pendingMerge.ziel.titel)
                        : ""
                }
                message={
                    pendingMerge ? (
                        <span style={{ whiteSpace: "pre-line" }}>
                            {(zugWeg(pendingMerge.quelle, pendingMerge.ziel) === "alsTodo"
                                ? alsTodoErklaerung
                                : verschmelzErklaerung)(pendingMerge.quelle.titel, pendingMerge.ziel.titel)}
                            {"\n\n"}
                            Andersherum gemeint? „Umkehren“ lässt stattdessen „
                            {pendingMerge.quelle.titel.trim() || "(ohne Betreff)"}“ bestehen.
                        </span>
                    ) : null
                }
                confirmLabel={
                    pendingMerge && zugWeg(pendingMerge.quelle, pendingMerge.ziel) === "alsTodo"
                        ? "Als To-do übernehmen"
                        : "Verschmelzen"
                }
                busy={mergeBusy}
                /* T-246: Welcher Vorgang führend ist, entscheidet nicht die
                   Ziehrichtung allein — „Umkehren" tauscht Quelle und Ziel;
                   Titel, Weg und Knopf leiten sich aus dem Paar neu ab. */
                extraAction={
                    pendingMerge
                        ? {
                              label: "⇄ Umkehren",
                              title: `Stattdessen bleibt „${pendingMerge.quelle.titel || "(ohne Betreff)"}“`,
                              onClick: () => setPendingMerge((p) => (p ? tauscheZug(p) : p)),
                          }
                        : undefined
                }
                onConfirm={() => void confirmMerge()}
                onCancel={() => setPendingMerge(null)}
            />
            {/* TO-DO AUF EINEN FREMDEN VORGANG GEZOGEN (PO 22.09.2026).
                Eigener Dialog neben dem Verschmelzen: Es ist eine andere
                Handlung — nichts verschwindet, etwas wechselt den Platz. Ton
                daher NICHT `danger`. */}
            <ConfirmDialog
                open={!!pendingTodoZug}
                title={
                    pendingTodoZug
                        ? verschiebeFrage(pendingTodoZug.todo.titel, pendingTodoZug.ziel.titel)
                        : ""
                }
                message={
                    pendingTodoZug ? (
                        <span style={{ whiteSpace: "pre-line" }}>
                            {verschiebeErklaerung(
                                pendingTodoZug.todo.titel,
                                pendingTodoZug.quelleTitel,
                                pendingTodoZug.ziel.titel,
                            )}
                        </span>
                    ) : null
                }
                confirmLabel="Verschieben"
                busy={todoZugBusy}
                onConfirm={() => void confirmTodoZug()}
                onCancel={() => setPendingTodoZug(null)}
            />
            {/* Duplikate zusammenführen (PO 21.09.2026). Eigener Dialog statt
                `ConfirmDialog`: Hier wird nicht nur bestätigt, sondern gewählt
                — welcher bleibt, und was nicht mitwandert. */}
            {dupOffen && dupKandidaten.length >= 2 && (
                <VorgaengeZusammenfuehrenDialog
                    kandidaten={dupKandidaten}
                    onClose={() => setDupOffen(false)}
                    onFertig={(zielId, verschwundene) => {
                        setGewaehlteIds(new Set());
                        // Bis zum 21.09.2026 sprang die Liste hier auf die
                        // Detailseite des Überlebenden. Das nimmt dem Anwender
                        // genau den Blick, den er sucht: Er will SEHEN, dass aus
                        // mehreren Zeilen eine geworden ist (PO: „muss in
                        // echtzeit angezeigt werden"). Also bleibt er stehen,
                        // und die Zeilen ändern sich unter ihm.
                        //
                        // `verschwundene` sind seit dem 22.09.2026 NUR die
                        // Vorgänge, die tatsächlich fort sind. Wer einzelne
                        // To-dos übernimmt, lässt den Quellvorgang mit dem Rest
                        // stehen — dann darf seine Zeile nicht verschwinden.
                        void zieheVerschmelzungNach(
                            zielId,
                            verschwundene,
                            // Was ausgewählt war, aber überlebt hat: verändert,
                            // nicht fort.
                            dupKandidaten
                                .map((k) => k.id)
                                .filter((id) => id !== zielId && !verschwundene.includes(id)),
                        );
                        setToast({
                            message: verschwundene.length
                                ? verschwundene.length === 1
                                    ? "Die beiden Vorgänge stehen jetzt als einer."
                                    : `${verschwundene.length + 1} Vorgänge stehen jetzt als einer.`
                                : "Die ausgewählten To-dos sind umgezogen.",
                            tone: "success",
                        });
                    }}
                />
            )}
            {/* TO-DO AUF TO-DO (PO 28.09.2026). Die Geschwister stehen als
                Kinder der Vorgangszeile schon da — keine Netzrunde nötig. */}
            {todoMerge &&
                (() => {
                    const geschwister =
                        rows.find((v) => v.kind === "vorgang" && v.id === todoMerge.vorgangId)?.children ?? [];
                    const quelle = geschwister.find((c) => c.id === todoMerge.quelleId)?.todo;
                    const kandidaten = geschwister
                        .filter((c) => c.id !== todoMerge.quelleId && c.todo)
                        .map((c) => ({ todo: c.todo!, zaehlung: "" }));
                    if (!quelle || kandidaten.length === 0) return null;
                    return (
                        <TodoZusammenfuehrenDialog
                            quelle={quelle}
                            kandidaten={kandidaten}
                            vorgewaehltesZielId={todoMerge.zielId}
                            // Beim Ziehen: Betreff vom gezogenen To-do (T-15).
                            betreffVon="quelle"
                            users={users}
                            onZusammengefuehrt={() => {
                                // Lokal nachziehen wie beim Verschmelzen — kein
                                // Refetch, die Scroll-Position bleibt.
                                void zieheVerschmelzungNach(todoMerge.vorgangId, []);
                            }}
                            onSchliessen={() => setTodoMerge(null)}
                        />
                    );
                })()}
            {/* Nicht-blockierende Fehlermeldung (statt window.alert). */}
            <Toast
                open={!!toast}
                message={toast?.message ?? ""}
                tone={toast?.tone ?? "info"}
                onClose={() => setToast(null)}
            />
        </div>
    );
};

export default VorgaengeMo;
